import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Map, View, MapBrowserEvent, Feature, MapEvent } from 'ol';
import { Geometry, Point } from 'ol/geom';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource, Cluster, XYZ, OSM } from 'ol/source';
import { fromLonLat, toLonLat, transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import RenderFeature from 'ol/render/Feature';
import {createEmpty, extend, isEmpty} from 'ol/extent';
import { FitOptions } from 'ol/View';
import { isEqual } from 'lodash';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
    getRenderScale,
    createEmptyVectorSource,
    createEmptyVectorLayer,
    createSnowmobilingIconFeature,
    createCommonPathFeatures,
    createPathIconFeatures,
    createEndcapIconFeature,
    createCommonPoiIconFeature,
    createPoiEventConnectorFeature,
    createPoiEventRowIconFeature,
    createPoiEventCountBadgeFeature,
    POI_EVENT_GROUP_PROPERTY,
    POI_LAYER_RENDER_BUFFER,
    createTemperaturePoiIconFeature,
    createCommonAoiIconFeature,
    getVisiblePaths,
    createSnowmobilePathFeatures,
    createGpsIndicatorFeature,
    postMessage,
    createCommonAoiFeatures
} from './utils';
import { Style, Icon, Circle as CircleStyle, Fill, Stroke, Text } from 'ol/style';
import {
    OBJECT_TYPE,
    OBJECT_ID,
    MAPBOX_ACCESS_TOKEN,
    MAPBOX_STYLE_USER,
    MAPBOX_STYLE_ID,
    MAPBOX_3D_STYLE_USER,
    MAPBOX_3D_STYLE_ID,
    MAPBOX_SATELLITE_STYLE_USER,
    MAPBOX_SATELLITE_STYLE_ID,
    MAPBOX_3D_SATELLITE_STYLE_USER,
    MAPBOX_3D_SATELLITE_STYLE_ID,
    ICONS,
    PROPS,
    COLORS,
} from './constants';
import { FeatureType, MapType, PathCategoryId, PathDirection, PathStatus } from './enums';
import {
    MapCategory,
    MapCategories,
    MapCategoryProps,
    FeatureGroup,
} from './interfaces';
import { getIconDataUri, hasIcon } from './icon-library/iconLibrary.generated';
import {
    PathGpsCoordinate,
    Path,
    Paths,
    Poi,
    Pois,
    SnowmobilePaths,
    SnowmobileSubPath,
    SnowmobileSubPaths,
    GpsCoordinate,
    PathGpsCoordinates,
    PoiCategories,
    PathCategories,
    Aois,
    AoiCategories,
    AoiGpsCoordinates,
    AoiGpsCoordinate,
    Aoi
} from '@sodraberget/shared/src/interfaces';
import { getTranslatedProperty } from "../src/utils"
import './Map.css';

type MapProps = {
    style?: any;
    onMessage?: (msg: string) => void;
    onNearestFeature?: (data: any) => void;
};

const isValidLonLat = (longitude: unknown, latitude: unknown): boolean => {
    if (longitude === null || longitude === undefined || longitude === '' ||
        latitude === null || latitude === undefined || latitude === '') {
        return false;
    }
    const lon = Number(longitude);
    const lat = Number(latitude);
    return Number.isFinite(lon) && Number.isFinite(lat) &&
        Math.abs(lon) <= 180 && Math.abs(lat) <= 90;
};

const MAX_POI_EVENTS = 3;
const POI_EVENT_LABEL_MAX_LENGTH = 9;
const POI_EVENT_SAME_POSITION_METERS = 50;

// Events without dates are treated as ongoing
const isUpcomingEvent = (event: { startAt?: string, endAt?: string }, now: number): boolean => {
    const end = Date.parse(event.endAt ?? event.startAt ?? '');
    return Number.isNaN(end) || end >= now;
};

const getEventStartTime = (event: any): number => {
    const start = Date.parse(event?.startAt ?? '');
    return Number.isNaN(start) ? Number.MAX_SAFE_INTEGER : start;
};

const formatEventDate = (value?: string): string => {
    const date = value ? new Date(value) : undefined;
    return date && !Number.isNaN(date.getTime()) ? `${date.getDate()}/${date.getMonth() + 1}` : '';
};

const truncateLabel = (text: string, maxLength: number = POI_EVENT_LABEL_MAX_LENGTH): string => (
    text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text
);

const MapComponent = (props: MapProps, ref: any) => {
    const {onMessage} = props;
    const debugLogging = process.env.REACT_APP_MAP_DEBUG === 'true';
    const debugLog = (...args: any[]) => {
        if (debugLogging) {
            console.log(...args);
        }
    };
    const onMessageRef = useRef(onMessage);
    //Selected language
    const selectedLanguage = useRef<string>('en');
    // Map
    const map = useRef<Map>();
    const mapRef = useRef<HTMLDivElement>(null);
    // 3D Map (Mapbox GL)
    const map3dRef = useRef<HTMLDivElement>(null);
    const map3d = useRef<mapboxgl.Map | null>(null);
    const map3dLoaded = useRef<boolean>(false);
    const sync3DAnimationFrame = useRef<number | null>(null);
    const pendingSync3D = useRef<boolean>(false);
    const updateMapSourcesRef = useRef<(type?: MapType, force?: boolean) => void>();
    // Tile layers
    const hdMapLayer = useRef<TileLayer<OSM>>();
    const sateliteLayer = useRef<TileLayer<XYZ>>();
    // Tile sources
    const hdMapSource = useRef<OSM>();
    // Vector layers
    const snowmobilePathLayer = useRef<any>();
    const highlightedSnowmobilePathLayer = useRef<any>();
    const pathLayer = useRef<any>();
    const aoiLayer = useRef<any>();
    const highlightedAoiLayer = useRef<any>();
    const highlightedPathLayer = useRef<any>();
    const highlightedPathIconLayer = useRef<any>();
    const poiLayer = useRef<any>();
    const highlightedPoiLayer = useRef<any>();
    const gpsPositionLayer = useRef<any>();
    const snowmobileIconLayer = useRef<any>();
    const hikingIconLayer = useRef<any>();
    const crossCountrySkiingIconLayer = useRef<any>();
    const mountainBikingIconLayer = useRef<any>();
    const runningIconLayer = useRef<any>();
    const ridingIconLayer = useRef<any>();
    const canoeIconLayer = useRef<any>();
    const chairLiftIconLayer = useRef<any>();
    const gondolaIconLayer = useRef<any>();
    const surfaceLiftIconLayer = useRef<any>();
    const beltLiftIconLayer = useRef<any>();
    const skiSlopeIconLayer = useRef<any>();

    // Vector sources
    const aoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedAoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const snowmobilePathSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedSnowmobilePathSource = useRef<VectorSource<Feature<Geometry>>>();
    const pathSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedPathSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedPathIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const poiSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedPoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const gpsPositionSource = useRef<VectorSource<Feature<Geometry>>>();
    const snowmobileIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const hikingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const crossCountrySkiingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const mountainBikingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const runningIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const ridingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const canoeIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const chairLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const gondolaIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const surfaceLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const beltLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const skiSlopeIconSource = useRef<VectorSource<Feature<Geometry>>>();

    // Data set by calling exposed functions
    const poiCategoryData = useRef<PoiCategories>({});
    const poiData = useRef<Pois>({});
    const aoiCategoryData = useRef<AoiCategories>({});
    const aoiData = useRef<Aois>({});
    const aoiGpsData = useRef<AoiGpsCoordinates>({});
    const pathCategoryData = useRef<PathCategories>({});
    const pathData = useRef<Paths>({});
    const snowmobilePathData = useRef<SnowmobilePaths>({});
    const snowmobileSubPathData = useRef<SnowmobileSubPaths>({});
    const pathGpsData = useRef<PathGpsCoordinates>({});
    const mapType = useRef<MapType>();
    const filterData = useRef<FeatureGroup[]>();
    const focusData = useRef<FeatureGroup[]>();
    const selectedType = useRef<string | undefined>();
    const selectedId = useRef<string | undefined>();
    const highlightedType = useRef<string | undefined>();
    const highlightedId = useRef<string | undefined>();
    const gpsPositionData = useRef<GpsCoordinate>();
    const gpsIsCentered = useRef<boolean>();
    const headingData = useRef<number>();
    const pathDirection = useRef<PathDirection | undefined>();
    // Data set by internal functions
    const pathCategoryProps = useRef<MapCategoryProps>();
    const poiCategoryProps = useRef<MapCategoryProps>();
    const aoiCategoryProps = useRef<MapCategoryProps>();
    // Where each grouped event is placed in the row below its POI, keyed by event feature id
    const poiEventRowLayout = useRef<Record<string, { poiId: string, index: number }>>({});

    const isThreeDMapType = (type?: MapType) => type === MapType.ThreeD || type === MapType.ThreeDSatelite;

    const setSelectedLanguage = (iso: string) => {
        selectedLanguage.current = iso;
    }

    const pickPreferredCandidate = (candidates: Array<{ type?: string, id?: string }>): { type?: string, id?: string } | undefined => {
        const validCandidates = candidates.filter((candidate) => !!candidate.type && !!candidate.id) as Array<{ type: string, id: string }>;
        if (!validCandidates.length) {
            return undefined;
        }

        const poiCandidate = validCandidates.find((candidate) => candidate.type === FeatureType.Poi);
        if (poiCandidate) {
            return poiCandidate;
        }

        if (selectedType.current === FeatureType.Path && selectedId.current) {
            const selectedPath = pathData.current[selectedId.current] as any;
            const childIds = new Set((selectedPath?.compositeChildPathIds ?? []).map((id: number | string) => `${id}`));
            if (childIds.size > 0) {
                const childCandidate = validCandidates.find((candidate) => (
                    candidate.type === FeatureType.Path && childIds.has(`${candidate.id}`)
                ));
                if (childCandidate) {
                    return childCandidate;
                }
            }
        }

        return validCandidates[0];
    };
    
    /**
     * Exposed function to set the data for POIs
     */
    const setPois = (pois: Pois, categories: PoiCategories) => {
        if (!isEqual(poiData.current, pois) || !isEqual(poiCategoryData.current, categories)) {
            if (categories) {
                setPoiCategoryProps(categories);
            }

            poiData.current = pois;
            poiCategoryData.current = categories;
            redraw();
        }
    };

    /**
     * Exposed function to set the data for aois
     */
    const setAois = (aois: Aois, categories: AoiCategories, coordinates: AoiGpsCoordinates) => {
        if (!isEqual(aoiData.current, aois) || !isEqual(aoiCategoryData.current, categories) || !isEqual(aoiGpsData.current, coordinates)) {
            if (categories) {
                setAoiCategoryProps(categories);
            }
            aoiData.current = aois;
            aoiCategoryData.current = categories;
            aoiGpsData.current = coordinates;
            redraw();
        }
    };

    /**
     * Exposed function to set the data for paths
     */
    const setPaths = (paths: Paths, categories: PathCategories, coordinates: PathGpsCoordinates) => {
        if (!isEqual(pathData.current, paths) || !isEqual(pathCategoryData.current, categories) || !isEqual(pathGpsData.current, coordinates)) {
            if (categories) {
                setPathCategoryProps(categories);
            }

            pathData.current = paths;
            pathCategoryData.current = categories;
            pathGpsData.current = coordinates;
            redraw();
        }
    };

    /**
     * Exposed function to set the data for snowmobile paths
     */
    const setSnowmobilePaths = (paths: SnowmobilePaths, subPaths: SnowmobileSubPaths) => {
        if (!isEqual(snowmobilePathData.current, paths) || !isEqual(snowmobileSubPathData.current, subPaths)) {
            snowmobilePathData.current = paths;
            snowmobileSubPathData.current = subPaths;
        }
    };

    /**
     * Exposed function to set the type of map
     */
    const setMapType = (type: MapType) => {
        if (mapType.current !== type) {
            updateMapSources(type, true);

            mapType.current = type;
        }
    };
    
    /**
     * Exposed function to filter the rendered features on the map
     */
    const setFilters = (filters: FeatureGroup[], shouldRefocus: boolean = true) => {
        debugLog('[MAP DEBUG] setFilters() called', filters, 'shouldRefocus:', shouldRefocus);
        if (!isEqual(filterData.current, filters)) {
            filterData.current = filters;

            redraw();
            // Only refocus when applying actual filters, not when clearing/initializing
            if (shouldRefocus && filters?.length) {
                refocus();
            }
        }
    };

    /**
     * Exposed function to focus on particular feature groups
     */
    const setFocus = (focus: FeatureGroup[], shouldRefocus: boolean = true) => {
        if (!isEqual(focusData.current, focus)) {
            focusData.current = focus;

            if (shouldRefocus) {
                refocus();
            }
        }
    };

    /**
     * Exposed function to set the selected feature on the map (feature to highlight and refocus on)
     */
    const setSelected = (type?: string, id?: string) => {
        selectedType.current = type;
        selectedId.current = id;

        // Selection can change while GPS position is unchanged.
        // Recalculate nearest point immediately so elevation marker follows
        // the newly selected path (e.g. composite -> child path switch).
        if (type === FeatureType.Path && id && gpsPositionData.current) {
            const { longitude, latitude } = gpsPositionData.current;
            reportNearestFeature(fromLonLat([longitude, latitude]));
        }

        // When deselecting (no type/id), only clear highlight without refocusing
        // This preserves the current zoom and position
        if (type === undefined && id === undefined) {
            setHighlighted(undefined, undefined);
            return;
        }

        // Highlight differs, render new highlight and then refocus
        if (highlightedType.current !== type || highlightedId.current !== id) {
            setHighlighted(type, id);
            setTimeout(() => {
                refocus();
            });
        }
        // Highlight doesn't change, just refocus
        else {
            refocus();
        }
    };

    /**
     * Exposed function to set the highlighted feature on the map
     */
    const setHighlighted = (type?: string, id?: string) => {
        //set opacity 0.5 to all unselcted features
        if (highlightedType.current !== type || highlightedId.current !== id) {
            highlightedType.current = type;
            highlightedId.current = id;
            const defaultOpacity = type !== undefined || id !== undefined ? 0.5: 1;
            //path icons
            snowmobileIconLayer.current?.setOpacity(defaultOpacity);
            snowmobileIconLayer.current?.changed();
            hikingIconLayer.current?.setOpacity(defaultOpacity);
            hikingIconLayer.current?.changed();
            mountainBikingIconLayer.current?.setOpacity(defaultOpacity);
            mountainBikingIconLayer.current?.changed();
            crossCountrySkiingIconLayer.current?.setOpacity(defaultOpacity);
            crossCountrySkiingIconLayer.current?.changed(); 
            runningIconLayer.current?.setOpacity(defaultOpacity);
            runningIconLayer.current?.changed(); 
            ridingIconLayer.current?.setOpacity(defaultOpacity);
            ridingIconLayer.current?.changed();
            canoeIconLayer.current?.setOpacity(defaultOpacity);
            canoeIconLayer.current?.changed();
            chairLiftIconLayer.current?.setOpacity(defaultOpacity);
            chairLiftIconLayer.current?.changed();
            gondolaIconLayer.current?.setOpacity(defaultOpacity);
            gondolaIconLayer.current?.changed();
            surfaceLiftIconLayer.current?.setOpacity(defaultOpacity);
            surfaceLiftIconLayer.current?.changed();
            beltLiftIconLayer.current?.setOpacity(defaultOpacity);
            beltLiftIconLayer.current?.changed();
            skiSlopeIconLayer.current?.setOpacity(defaultOpacity);
            skiSlopeIconLayer.current?.changed();
            //paths layer
            pathLayer.current?.setOpacity(defaultOpacity);
            pathLayer.current?.changed();
            snowmobilePathLayer.current?.setOpacity(defaultOpacity);
            snowmobilePathLayer.current?.changed();
            //aois layer
            aoiLayer.current?.setOpacity(defaultOpacity);
            aoiLayer.current?.changed();
            //pois layer
            poiLayer.current?.setOpacity(defaultOpacity);
            poiLayer.current?.changed();

            redraw();
        }
    };

    /**
     * Exposed function to set the current GPS position and heading on the map
     */
    const setGpsPosition = (position?: GpsCoordinate, heading?: number) => {
        if (!isEqual(gpsPositionData.current, position) || !isEqual(headingData.current, heading)) {
            gpsPositionSource.current?.clear();
          
            if (position) {
                const {longitude, latitude} = position;
                const coordinate = fromLonLat([longitude, latitude]);
                const point = new Point(coordinate);
          
                gpsPositionSource.current?.addFeatures([createGpsIndicatorFeature(point, heading)]);
                reportNearestFeature(coordinate);
            }

            // Invalidate centered state if position has changed
            if (!isEqual(gpsPositionData.current, position)) {
                gpsIsCentered.current = false;
                const msg = JSON.stringify({centered: false});
                postMessage(msg);
                onMessageRef.current?.(msg);

                gpsPositionData.current = position;
            }

            headingData.current = heading;

            // Update 3D GPS marker (with heading for rotation)
            if (isThreeDMapType(mapType.current) && map3d.current && map3dLoaded.current && position) {
                const headingDeg = heading != null ? (heading * 180 / Math.PI) : 0;
                const gpsGJ: any = {
                    type: 'FeatureCollection',
                    features: [{ type: 'Feature', properties: { heading: headingDeg }, geometry: { type: 'Point', coordinates: [position.longitude, position.latitude] } }]
                };
                if (map3d.current.getSource('gps-source')) {
                    (map3d.current.getSource('gps-source') as mapboxgl.GeoJSONSource).setData(gpsGJ);
                }
            }
        }
    };

    /**
     * Exposed function to center the view on the current GPS position
     */
    const centerGps = () => {
        // 3D mode
        if (isThreeDMapType(mapType.current) && map3d.current && gpsPositionData.current) {
            map3d.current.flyTo({
                center: [gpsPositionData.current.longitude, gpsPositionData.current.latitude],
                duration: 500,
            });
            gpsIsCentered.current = true;
            const msg = JSON.stringify({centered: true});
            postMessage(msg);
            onMessageRef.current?.(msg);
            return;
        }

        if (!gpsPositionSource.current?.getFeatures().length) {
            return;
        }
        const view = map.current?.getView();
        view?.fit(gpsPositionSource.current.getExtent());
        view?.setRotation(0);

        gpsIsCentered.current = true;

        const msg = JSON.stringify({centered: true});
        postMessage(msg);
        onMessageRef.current?.(msg);
    };

    /**
     * Exposed function to zoom in or out by a specified amount
     */
    const adjustZoom = (amount: number) => {
        if (isThreeDMapType(mapType.current) && map3d.current) {
            map3d.current.zoomTo(map3d.current.getZoom() + amount, {
                duration: 100,
            });
            return;
        }

        const view = map.current?.getView();
        if (view === undefined) {
            return;
        }

        const zoom = view.getZoom();
        if (zoom === undefined) {
            return;
        }

        view.animate({
            zoom: zoom + amount,
            duration: 100
        });
    };

    /**
     * Exposed function to set the path direction indication
     */
    const setPathDirection = (direction?: PathDirection, redraw?: boolean) => {
        pathDirection.current = direction;

        if (redraw && highlightedType.current !== undefined && highlightedId.current !== undefined) {
            redrawHighlight();
        }

        if (isThreeDMapType(mapType.current)) {
            scheduleSync3DData();
        }
    };

    /**
     * Exposed function to center the map on a given coordinate and zoom level
     */
    const setCenter = (longitude: number, latitude: number, zoom?: number) => {
        // 3D mode
        if (isThreeDMapType(mapType.current) && map3d.current) {
            map3d.current.jumpTo({
                center: [longitude, latitude],
                zoom: zoom ?? map3d.current.getZoom(),
            });
            return;
        }

        const view = map.current?.getView();
        if (!view) return;
        view.animate({
            center: fromLonLat([longitude, latitude]),
            zoom: zoom ?? view.getZoom(),
            duration: 0,
        });
    };

    // Expose functions through the component reference
    /**
     * Updates the temperature for temperature POIs
     */
    const updateTemperatures = (temperatures: Array<{id: string, temperature: number}>) => {
        if (!poiSource.current) return;
        
        temperatures.forEach(({id, temperature}) => {
            // Update POI data
            if (poiData.current[id]) {
                (poiData.current[id] as any).temperature = temperature;
            }
        });
        
        // Redraw to update display
        redraw();
    };

    useImperativeHandle(ref, () => ({
        setAois,
        setPois,
        setPaths,
        setSnowmobilePaths,
        setMapType,
        setFilters,
        setFocus,
        setSelected,
        setHighlighted,
        setGpsPosition,
        centerGps,
        adjustZoom,
        setCenter,
        setPathDirection,
        setSelectedLanguage,
        updateTemperatures,
    }));

    // Expose functions through the window scope
    (window as any).setAois = setAois;
    (window as any).setPois = setPois;
    (window as any).setPaths = setPaths;
    (window as any).setSnowmobilePaths = setSnowmobilePaths;
    (window as any).setMapType = setMapType;
    (window as any).setFilters = setFilters;
    (window as any).setFocus = setFocus;
    (window as any).setSelected = setSelected;
    (window as any).setHighlighted = setHighlighted;
    (window as any).setGpsPosition = setGpsPosition;
    (window as any).centerGps = centerGps;
    (window as any).adjustZoom = adjustZoom;
    (window as any).setCenter = setCenter;
    (window as any).setPathDirection = setPathDirection;
    (window as any).setSelectedLanguage = setSelectedLanguage;
    (window as any).updateTemperatures = updateTemperatures;

    // Initial setup
    useEffect(() => {
        // Create Standard Definition map source (lantmäteriet)
        hdMapSource.current = new XYZ({
            //url: `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_USER}/${MAPBOX_STYLE_ID}/tiles/256/{z}/{x}/{y}?access_token=${MAPBOX_ACCESS_TOKEN}`,
            // To use for retina displays (@2x) tiles för retina-displays
            url: `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_USER}/${MAPBOX_STYLE_ID}/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_ACCESS_TOKEN}`,
            tilePixelRatio: 2, // important to use if you use @2x URL

            attributions: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
            maxZoom: 22, // Mapbox styles only support up to 22 zoom level
            tileSize: 256, // Standard for Mapbox raster tiles.
        });

        hdMapLayer.current = new TileLayer({
            source: hdMapSource.current,
            opacity: 1.0, // Keep or adjust transparency as needed
        });
        // Set hdMapLayer to visible if the default MapType is 'Map', otherwise set its visibility to false.
        // If mapType.current is initialized as MapType.Map, make hdMapLayer visible immediately.
        hdMapLayer.current?.setVisible(mapType.current === MapType.Map);

        // Create a layer for the satelite map
        sateliteLayer.current = new TileLayer({
            source: new XYZ({
                attributions: [
                    '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a>',
                    '© <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                    '<strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
                ],
                attributionsCollapsible: false,
                url: `https://api.mapbox.com/styles/v1/${MAPBOX_SATELLITE_STYLE_USER}/${MAPBOX_SATELLITE_STYLE_ID}/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_ACCESS_TOKEN}`,
                tilePixelRatio: 2,
                maxZoom: 22,
                tileSize: 256,
            }),
            opacity: 1.0,
        });
        sateliteLayer.current?.setVisible(false);

        // Zoom thresholds for icon visibility
        const PATH_ICON_MIN_ZOOM = 15;
        const POI_MIN_ZOOM = 11;
        const POI_TEXT_MIN_ZOOM = 15; // POI names visible at higher zoom
        const TEMPERATURE_MIN_ZOOM = 11; // Temperature POIs visible earlier than other POIs
        const ENDCAP_MIN_ZOOM = 15; // Start/end markers visible at zoom >= 15

        // Create shared style function for path layers (handles both lines and endcap icons)
        // This function hides endcap icons when zoomed out but always shows path lines
        const createPathLayerStyleFunction = () => (feature: any, resolution: number) => {
            // Check if this feature has a __baseStyle (endcap icon)
            const baseStyle = feature.get('__baseStyle');
            
            if (baseStyle) {
                // This is an endcap icon - apply zoom threshold
                const view = map.current?.getView();
                const zoom = view?.getZoom();

                if (zoom === undefined || zoom < ENDCAP_MIN_ZOOM) {
                    return null; // Hide endcap when zoomed out
                }
                
                // Show endcap when zoomed in
                if (typeof baseStyle === 'function') {
                    try {
                        return baseStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return baseStyle;
            }

            // This is a path line or other feature - check for explicit style
            const featStyle = feature.getStyle?.();
            if (featStyle) {
                if (typeof featStyle === 'function') {
                    try {
                        return featStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return featStyle;
            }

            // Use default rendering
            return null;
        };

        // Create a new layers to render snowmobile paths on
        snowmobilePathSource.current = createEmptyVectorSource();
        snowmobilePathLayer.current = createEmptyVectorLayer(snowmobilePathSource.current, createPathLayerStyleFunction());
        highlightedSnowmobilePathSource.current = createEmptyVectorSource();
        highlightedSnowmobilePathLayer.current = createEmptyVectorLayer(highlightedSnowmobilePathSource.current, createPathLayerStyleFunction());

        // Create a new layers to render paths on
        pathSource.current = createEmptyVectorSource();
        pathLayer.current = createEmptyVectorLayer(pathSource.current, createPathLayerStyleFunction());
        highlightedPathSource.current = createEmptyVectorSource();
        highlightedPathLayer.current = createEmptyVectorLayer(highlightedPathSource.current, createPathLayerStyleFunction());

        // Create a new layers to render aois on
        aoiSource.current = createEmptyVectorSource();
        aoiLayer.current = createEmptyVectorLayer(aoiSource.current);
        highlightedAoiSource.current = createEmptyVectorSource();
        highlightedAoiLayer.current = createEmptyVectorLayer(highlightedAoiSource.current);

        // Create shared style function for POI layers
        // This function hides POI icons when zoomed out and renders them when zoomed in
        const createPoiStyleFunction = () => (feature: any, resolution: number) => {
            const view = map.current?.getView();
            const zoom = view?.getZoom();

            // If zoom is not yet available, don't render
            if (zoom === undefined) {
                return null;
            }

            // Check if this is a temperature POI (has __isTemperature property)
            const isTemperature = feature.get('__isTemperature');
            const minZoom = isTemperature ? TEMPERATURE_MIN_ZOOM : POI_MIN_ZOOM;

            // Hide POI icons when zoomed out
            if (zoom < minZoom) {
                return null;
            }

            // Events grouped below a POI: the row is shown when zoomed in, a count badge otherwise
            const eventGroupRole = feature.get(POI_EVENT_GROUP_PROPERTY);
            if (eventGroupRole) {
                const showRow = zoom >= POI_TEXT_MIN_ZOOM;
                if ((eventGroupRole === 'badge') === showRow) {
                    return null;
                }
                return feature.get('__baseStyle') ?? null;
            }

            // Get the stored base style from feature property
            const baseStyle = feature.get('__baseStyle');
            if (baseStyle) {
                if (typeof baseStyle === 'function') {
                    try {
                        return baseStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                
                // Temperature POIs have a single image style, not [backplate, text, icon].
                if (Array.isArray(baseStyle) && zoom < POI_TEXT_MIN_ZOOM) {
                    return baseStyle.filter((style: Style) => !style.getText());
                }
                
                return baseStyle;
            }

            // Fallback: check for explicit style set via setStyle()
            const featStyle = feature.getStyle?.();
            if (featStyle) {
                if (typeof featStyle === 'function') {
                    try {
                        return featStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return featStyle;
            }

            // Otherwise return null to use default rendering
            return null;
        };

        // Create a new layers to render pois on
        poiSource.current = createEmptyVectorSource();
        poiLayer.current = createEmptyVectorLayer(poiSource.current, createPoiStyleFunction(), POI_LAYER_RENDER_BUFFER);
        const poiClusterSource = new Cluster({
            distance: 60 * getRenderScale(),
            source: poiSource.current,
            geometryFunction: (feature) => {
                if (feature.get(POI_EVENT_GROUP_PROPERTY) || feature.get('__isTemperature')) {
                    return null;
                }
                const poi = poiData.current[feature.get(OBJECT_ID)] as (Poi & { sourceType?: string }) | undefined;
                if (!poi || poi.sourceType === 'event' || `${poi.id}`.startsWith('event-')) {
                    return null;
                }
                const geometry = feature.getGeometry();
                return geometry instanceof Point ? geometry : null;
            },
        });
        const clusterStyles = new globalThis.Map<number, Style>();
        const poiClusterLayer = new VectorLayer({
            source: poiClusterSource,
            style: (feature, resolution) => {
                const zoom = map.current?.getView().getZoomForResolution(resolution);
                if (zoom === undefined || zoom >= POI_MIN_ZOOM) {
                    return undefined;
                }
                const size = (feature.get('features') as Feature<Geometry>[]).length;
                let style = clusterStyles.get(size);
                if (!style) {
                    const scale = getRenderScale();
                    style = new Style({
                        image: new CircleStyle({
                            radius: (22 + Math.min(`${size}`.length - 1, 3) * 3) * scale,
                            fill: new Fill({ color: COLORS.POIS.DEFAULT || '#19456B' }),
                            stroke: new Stroke({ color: '#FFFFFF', width: 3 * scale }),
                        }),
                        text: new Text({
                            text: `${size}`,
                            font: 'bold 18px sans-serif',
                            scale,
                            textAlign: 'center',
                            textBaseline: 'middle',
                            fill: new Fill({ color: '#FFFFFF' }),
                        }),
                    });
                    clusterStyles.set(size, style);
                }
                return style;
            },
        });
        highlightedPoiSource.current = createEmptyVectorSource();
        highlightedPoiLayer.current = createEmptyVectorLayer(highlightedPoiSource.current, createPoiStyleFunction(), POI_LAYER_RENDER_BUFFER);

        // Create a new layer to render GPS poisition on
        gpsPositionSource.current = createEmptyVectorSource();
        gpsPositionLayer.current = createEmptyVectorLayer(gpsPositionSource.current);

        // Cache for rounded-rect backplate canvases keyed by "color|size"
        const backplateCanvasCache: Record<string, HTMLCanvasElement> = {};

        const createRoundedRectCanvas = (size: number, borderColor: string, opacity: number): HTMLCanvasElement => {
            const cacheKey = `${borderColor}|${size}|${opacity}`;
            if (backplateCanvasCache[cacheKey]) return backplateCanvasCache[cacheKey];

            const canvasSize = size * 2;
            const canvas = document.createElement('canvas');
            canvas.width = canvasSize;
            canvas.height = canvasSize;
            const ctx = canvas.getContext('2d')!;
            const borderWidth = Math.max(2, size * 0.06);
            const radius = size * 0.35; // corner radius
            const inset = borderWidth / 2;

            ctx.globalAlpha = opacity;

            // Rounded rect path
            ctx.beginPath();
            ctx.moveTo(inset + radius, inset);
            ctx.lineTo(canvasSize - inset - radius, inset);
            ctx.arcTo(canvasSize - inset, inset, canvasSize - inset, inset + radius, radius);
            ctx.lineTo(canvasSize - inset, canvasSize - inset - radius);
            ctx.arcTo(canvasSize - inset, canvasSize - inset, canvasSize - inset - radius, canvasSize - inset, radius);
            ctx.lineTo(inset + radius, canvasSize - inset);
            ctx.arcTo(inset, canvasSize - inset, inset, canvasSize - inset - radius, radius);
            ctx.lineTo(inset, inset + radius);
            ctx.arcTo(inset, inset, inset + radius, inset, radius);
            ctx.closePath();

            // White fill
            ctx.fillStyle = 'white';
            ctx.fill();

            // Colored border
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = borderWidth;
            ctx.stroke();

            backplateCanvasCache[cacheKey] = canvas;
            return canvas;
        };

        // Create shared style function for all path icon layers
        // This function hides icons when zoomed out and renders them when zoomed in
        const createPathIconStyleFunction = () => (feature: any, resolution: number) => {
            const view = map.current?.getView();
            const zoom = view?.getZoom();

            // If zoom is not yet available, don't render
            if (zoom === undefined) return null;

            // Hide icons when zoomed out
            if (zoom < PATH_ICON_MIN_ZOOM) return null;

            // Otherwise build a style from icon properties placed on the feature
            const src = feature.get && (feature.get('iconSrc') || feature.get('icon'));
            if (!src) return null;

            const scale = feature.get('iconScale') ?? getRenderScale() * 0.7;
            const anchor = feature.get('iconAnchor') ?? [0.5, 0.5];
            const anchorUnits = feature.get('iconAnchorUnits') ?? 'fraction';
            const opacity = feature.get('iconOpacity') ?? 1;
            const iconColor = feature.get('iconColor') || '#3b82f6';

            // Rounded-rect backplate: white fill + colored border
            const backplateSize = 36;
            const pixelSize = Math.round(backplateSize * scale * 2);
            const backplateCanvas = createRoundedRectCanvas(backplateSize * scale, iconColor, opacity);
            const backplateStyle = new Style({
                image: new Icon({
                    img: backplateCanvas,
                    size: [pixelSize, pixelSize],
                    scale: 1,
                    anchor: [0.5, 0.5],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                    opacity: 1, // opacity already baked into canvas
                }),
            });

            // The icon itself, tinted with the path color (scaled down relative to backplate)
            const iconScale = scale * 0.585;
            const iconStyle = new Style({
                image: new Icon({
                    src,
                    scale: iconScale,
                    anchor,
                    anchorXUnits: anchorUnits,
                    anchorYUnits: anchorUnits,
                    opacity,
                    color: iconColor || undefined,
                }),
            });

            return [backplateStyle, iconStyle];
        };

        // Create a new layer to render snowmobile icons (no clustering) - visibility controlled by zoom
        snowmobileIconSource.current = createEmptyVectorSource();
        snowmobileIconLayer.current = createEmptyVectorLayer(snowmobileIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render highlighted path icons
        highlightedPathIconSource.current = createEmptyVectorSource();
        highlightedPathIconLayer.current = createEmptyVectorLayer(highlightedPathIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render hiking icons (no clustering) - visibility controlled by zoom
        hikingIconSource.current = createEmptyVectorSource();
        hikingIconLayer.current = createEmptyVectorLayer(hikingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render cross country skiing icons (no clustering) - visibility controlled by zoom
        crossCountrySkiingIconSource.current = createEmptyVectorSource();
        crossCountrySkiingIconLayer.current = createEmptyVectorLayer(crossCountrySkiingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render mountain biking icons (no clustering) - visibility controlled by zoom
        mountainBikingIconSource.current = createEmptyVectorSource();
        mountainBikingIconLayer.current = createEmptyVectorLayer(mountainBikingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render running icons (no clustering) - visibility controlled by zoom
        runningIconSource.current = createEmptyVectorSource();
        runningIconLayer.current = createEmptyVectorLayer(runningIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render riding icons (no clustering) - visibility controlled by zoom
        ridingIconSource.current = createEmptyVectorSource();
        ridingIconLayer.current = createEmptyVectorLayer(ridingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render canoe icons (no clustering) - visibility controlled by zoom
        canoeIconSource.current = createEmptyVectorSource();
        canoeIconLayer.current = createEmptyVectorLayer(canoeIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render chair lift icons (no clustering) - visibility controlled by zoom
        chairLiftIconSource.current = createEmptyVectorSource();
        chairLiftIconLayer.current = createEmptyVectorLayer(chairLiftIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render gondola icons (no clustering) - visibility controlled by zoom
        gondolaIconSource.current = createEmptyVectorSource();
        gondolaIconLayer.current = createEmptyVectorLayer(gondolaIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render surface lift icons (no clustering) - visibility controlled by zoom
        surfaceLiftIconSource.current = createEmptyVectorSource();
        surfaceLiftIconLayer.current = createEmptyVectorLayer(surfaceLiftIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render belt lift icons (no clustering) - visibility controlled by zoom
        beltLiftIconSource.current = createEmptyVectorSource();
        beltLiftIconLayer.current = createEmptyVectorLayer(beltLiftIconSource.current, createPathIconStyleFunction());
        
        //Create a new layer to render ski slope icons (no clustering) - visibility controlled by zoom
        skiSlopeIconSource.current = createEmptyVectorSource();
        skiSlopeIconLayer.current = createEmptyVectorLayer(skiSlopeIconSource.current, createPathIconStyleFunction());

        // Create the OL Map
        map.current = new Map({
            target: mapRef.current!,
            layers: [
                hdMapLayer.current,
                sateliteLayer.current,
                snowmobilePathLayer.current,
                pathLayer.current,
                aoiLayer.current,
                snowmobileIconLayer.current,
                hikingIconLayer.current,
                mountainBikingIconLayer.current,
                crossCountrySkiingIconLayer.current,
                runningIconLayer.current,
                ridingIconLayer.current,
                canoeIconLayer.current,
                chairLiftIconLayer.current,
                gondolaIconLayer.current,
                surfaceLiftIconLayer.current,
                beltLiftIconLayer.current,
                highlightedSnowmobilePathLayer.current,
                highlightedPathLayer.current,
                highlightedPathIconLayer.current,
                highlightedAoiLayer.current,
                poiLayer.current,
                poiClusterLayer,
                highlightedPoiLayer.current,
                gpsPositionLayer.current,
                skiSlopeIconLayer.current,
            ],
            view: new View({
                center: fromLonLat([
                    parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || '20.2630'),
                    parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || '63.8258')
                ]),
                zoom: parseInt(process.env.REACT_APP_MAP_DEFAULT_ZOOM || '14', 10),
                minZoom: 6,
                maxZoom: 20,
            }),
            ...(process.env.REACT_APP_STANDALONE !== 'true' && {controls: []}),
        });

        const handleSingleClick = (event: MapBrowserEvent<any>) => {
            const cluster = map.current?.forEachFeatureAtPixel(
                event.pixel,
                (feature) => feature,
                {
                    layerFilter: (layer) => layer === poiClusterLayer,
                    hitTolerance: 11 * getRenderScale(),
                },
            );
            if (cluster) {
                const extent = createEmpty();
                (cluster.get('features') as Feature<Geometry>[]).forEach((feature) => {
                    const geometry = feature.getGeometry();
                    if (geometry) {
                        extend(extent, geometry.getExtent());
                    }
                });
                map.current?.getView().fit(extent, {
                    padding: [80, 80, 80, 80],
                    maxZoom: POI_MIN_ZOOM,
                    duration: 400,
                });
                return;
            }
            const hitCandidates: Array<{ type?: string, id?: string }> = [];

            // Itterate each point and see if we hit any feature
            map.current?.forEachFeatureAtPixel(
                event.pixel,
                (rootFeature) => {
                    const features = [rootFeature, ...(rootFeature.get('features') ?? [])];
                    features.forEach((feature) => {
                        const type = feature.get(OBJECT_TYPE);
                        const id = feature.get(OBJECT_ID);

                        if (type && id) {
                            hitCandidates.push({
                                type,
                                id: `${id}`,
                            });
                        }
                    });
                },
                {
                    hitTolerance: 11 * getRenderScale(),
                },
            );

            const hit = pickPreferredCandidate(hitCandidates);

            // Check if we have data within the detected feature, early return if not
            if (!hit) {
                const msg = JSON.stringify({deselect: true});
                postMessage(msg);
                onMessageRef.current?.(msg);
                return;
            }

            const type = hit.type;
            const id = hit.id;

            const msg = JSON.stringify({type, id});
            postMessage(msg);
            onMessageRef.current?.(msg);
        };

        const handlePointerDrag = (_event: MapBrowserEvent<any>) => {
            if (gpsIsCentered.current) {
                const msg = JSON.stringify({centered: false});
                postMessage(msg);
                onMessageRef.current?.(msg);

                gpsIsCentered.current = false;
            }
        };

        let lastLoggedZoom: number | undefined;
        const handleMoveEnd = (_event: MapEvent) => {
            updateMapSourcesRef.current?.(mapType.current);

            const zoom = map.current?.getView().getZoom();
            if (zoom !== undefined && zoom !== lastLoggedZoom) {
                lastLoggedZoom = zoom;
                const debugZoom = Math.round(zoom * 100) / 100;
                console.log(`[MAP] zoom ${debugZoom}`);
                postMessage(JSON.stringify({debugZoom}));
            }
        };

        // Register OL Map events
        map.current.on('singleclick', handleSingleClick);
        map.current.on('pointerdrag', handlePointerDrag);
        map.current.on('moveend', handleMoveEnd);

        // Expose for quick debugging in the browser console during development
        try {
            (window as any).__olMap = map.current;
        } catch (e) {
            // noop in non-browser environments
        }

        // Signal to React Native that the map is ready to receive commands
        postMessage(JSON.stringify({ mapReady: true }));

        return () => {
            poiClusterSource.setSource(null);
            if (sync3DAnimationFrame.current !== null) {
                window.cancelAnimationFrame(sync3DAnimationFrame.current);
                sync3DAnimationFrame.current = null;
            }
            pendingSync3D.current = false;

            if (map.current) {
                map.current.un('singleclick', handleSingleClick);
                map.current.un('pointerdrag', handlePointerDrag);
                map.current.un('moveend', handleMoveEnd);
                map.current.setTarget(undefined);
                map.current = undefined;
            }

            destroy3DMap();
        };
    }, []);

    /**
     * Updates map sources based on map type
     */
    const updateMapSources = (type?: MapType, force?: boolean) => {
        switch (type) {
            case MapType.Map:
                // Sync position from 3D if coming from ThreeD
                if (isThreeDMapType(mapType.current) && map3d.current) {
                    const center3d = map3d.current.getCenter();
                    const zoom3d = map3d.current.getZoom();
                    map.current?.getView().setCenter(fromLonLat([center3d.lng, center3d.lat]));
                    map.current?.getView().setZoom(zoom3d);
                }
                destroy3DMap();
                sateliteLayer.current?.setVisible(false);
                hdMapLayer.current?.setVisible(true);
                if (mapRef.current) mapRef.current.style.display = 'flex';
                if (map3dRef.current) map3dRef.current.style.display = 'none';
                break;

            case MapType.Satelite:
                // Sync position from 3D if coming from ThreeD
                if (isThreeDMapType(mapType.current) && map3d.current) {
                    const center3d = map3d.current.getCenter();
                    const zoom3d = map3d.current.getZoom();
                    map.current?.getView().setCenter(fromLonLat([center3d.lng, center3d.lat]));
                    map.current?.getView().setZoom(zoom3d);
                }
                destroy3DMap();
                hdMapLayer.current?.setVisible(false);
                sateliteLayer.current?.setVisible(true);
                if (mapRef.current) mapRef.current.style.display = 'flex';
                if (map3dRef.current) map3dRef.current.style.display = 'none';
                break;

            case MapType.ThreeD:
            case MapType.ThreeDSatelite:
                if (mapRef.current) mapRef.current.style.display = 'none';
                if (map3dRef.current) map3dRef.current.style.display = 'flex';
                init3DMap(type);
                break;

            default:
                break;
        }
    };
    updateMapSourcesRef.current = updateMapSources;

    /**
     * Resolve category props through a single shared registry lookup for both POI and event data.
     * Event categories are normalized to the same naming convention as POI categories, while older
     * compatibility aliases remain supported until all front-end data is aligned.
     */
    const resolveCategoryProps = (category: { identifier?: string; iconKey?: string | null }) => {
        const aliasKeys: string[] = [];

        const normalizeKey = (value: string) => value
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');

        const addAlias = (value?: string) => {
            if (!value) {
                return;
            }

            const normalized = normalizeKey(value);
            if (!normalized || aliasKeys.includes(normalized)) {
                return;
            }

            aliasKeys.push(normalized);
        };

        if (category.identifier) {
            addAlias(category.identifier);

            const normalizedIdentifier = normalizeKey(category.identifier);
            if (normalizedIdentifier.startsWith('EVENT_CATEGORY_')) {
                const legacyAlias = normalizedIdentifier.replace(/^EVENT_CATEGORY_/, '');
                addAlias(legacyAlias);
                addAlias(`EVENT_${legacyAlias}`);
                addAlias(`EVENT_ICON_${legacyAlias}`);
            }
        }

        if (category.iconKey) {
            addAlias(category.iconKey);
            addAlias(`EVENT_${category.iconKey}`);
            addAlias(`EVENT_ICON_${category.iconKey}`);
        }

        for (const aliasKey of aliasKeys) {
            const props = (PROPS.POIS as any)[aliasKey];
            if (props) {
                return props;
            }
        }

        return undefined;
    };

    /**
     * Builds category props from the shared icon library (icons/icons.json) when the category has an
     * iconKey/color set by a super admin. Falls back to the legacy PROPS registry otherwise.
     *  - badge: coloured circle with white glyph (POIs, events) – not tinted when rendered.
     *  - glyph: white glyph that the renderer tints with the category colour (paths, AOIs).
     */
    const applyLibraryAppearance = (
        category: MapCategory,
        fallback: { color: string; icon: string; closedIcon: string } | undefined,
        defaultColor: string,
        mode: 'badge' | 'glyph',
        glyphSize: number,
    ) => {
        const color = category.color || fallback?.color || defaultColor;
        if (!hasIcon(category.iconKey)) {
            return fallback ? { ...fallback, color } : undefined;
        }
        const icon = mode === 'badge'
            ? getIconDataUri(category.iconKey, { color: '#FFFFFF', background: color, size: 48 })!
            : getIconDataUri(category.iconKey, { color: '#FFFFFF', size: glyphSize })!;
        return { color, icon, closedIcon: icon };
    };

    /**
     * Sets up a map of the properties associated with each PoI category
     */
    const setPoiCategoryProps = (poiCategories: MapCategories) => {
        poiCategoryProps.current = {};
        for(const [key, value] of Object.entries(poiCategories)) {
            const props = applyLibraryAppearance(value, resolveCategoryProps(value), COLORS.POIS.DEFAULT || '#19456B', 'badge', 48);
            if (!props) {
                console.warn(`Unknown POI category: ${value.identifier}, using default fallback`);
                poiCategoryProps.current[key] = {
                    color: COLORS.POIS.DEFAULT || '#666666',
                    icon: 'openlayers/icons/pois/poi-default.png',
                    closedIcon: 'openlayers/icons/pois/poi-default.png'
                };
            } else {
                poiCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Sets up a map of the properties associated with each Path category
     */
    const setPathCategoryProps = (pathCategories: MapCategories) => {
        pathCategoryProps.current = {};
        for(const [key, value] of Object.entries(pathCategories)) {
            // Save Identifier data for this path id
            const props = applyLibraryAppearance(value, (PROPS.PATHS as any)[value.identifier], COLORS.PATHS.DEFAULT || '#19456B', 'glyph', 112);
            if (!props) {
                console.warn(`Unknown Path category: ${value.identifier}, using default fallback`);
                pathCategoryProps.current[key] = {
                    color: COLORS.PATHS.DEFAULT || '#666666',
                    icon: '',
                    closedIcon: ''
                };
            } else {
                pathCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Sets up a map of the properties associated with each Path category
     */
    const setAoiCategoryProps = (aoiCategories: MapCategories) => {
        aoiCategoryProps.current = {};
        for(const [key, value] of Object.entries(aoiCategories)) {
            // Save Identifier data for this aoi id
            const props = applyLibraryAppearance(value, (PROPS.AOIS as any)[value.identifier], COLORS.AOIS.DEFAULT || '#82A996', 'glyph', 60);
            if (!props) {
                console.warn(`Unknown AOI category: ${value.identifier}, using default fallback`);
                aoiCategoryProps.current[key] = {
                    color: COLORS.AOIS.DEFAULT || '#666666',
                    icon: '',
                    closedIcon: ''
                };
            } else {
                aoiCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Redraws all the map sources given the current filters, selection, etc.
     */
    const redraw = () => {
        aoiSource.current?.clear();
        pathSource.current?.clear();
        snowmobilePathSource.current?.clear();
        poiSource.current?.clear();
        snowmobileIconSource.current?.clear();
        hikingIconSource.current?.clear();
        crossCountrySkiingIconSource.current?.clear();
        mountainBikingIconSource.current?.clear();
        runningIconSource.current?.clear();
        ridingIconSource.current?.clear();
        canoeIconSource.current?.clear();
        chairLiftIconSource.current?.clear();
        gondolaIconSource.current?.clear();
        surfaceLiftIconSource.current?.clear();
        beltLiftIconSource.current?.clear();
        skiSlopeIconSource.current?.clear();
        
        if (!filterData.current?.length) {
            drawPaths([]);
            drawSnowmobilePaths([]);
            drawPois([]);
            drawAois([]);
        } else {
            filterData.current.forEach((mapFilter: FeatureGroup) => {
                switch (mapFilter.type) {
                    case FeatureType.Path:
                        drawPaths(mapFilter.ids);
                        break;
            
                    case FeatureType.SnowmobilePath:
                        drawSnowmobilePaths(mapFilter.ids);
                        break;

                    case FeatureType.SnowmobileSubPath:
                        drawSnowmobileSubPaths(mapFilter.ids);
                        break;
            
                    case FeatureType.Poi:
                        drawPois(mapFilter.ids);
                        break;
                    
                    case FeatureType.Aoi:
                        drawAois(mapFilter.ids);
                        break;
            
                    default:
                        break;
                }
            });
        }

        redrawHighlight();

        // Sync data to 3D map if active
        if (isThreeDMapType(mapType.current)) {
            scheduleSync3DData();
        }
    };

    /**
     * Schedules one 3D sync per animation frame.
     * Prevents repeated heavy syncs when several map actions happen close together.
     */
    function scheduleSync3DData() {
        if (!isThreeDMapType(mapType.current)) {
            return;
        }

        pendingSync3D.current = true;
        if (sync3DAnimationFrame.current !== null) {
            return;
        }

        sync3DAnimationFrame.current = window.requestAnimationFrame(() => {
            sync3DAnimationFrame.current = null;

            if (!pendingSync3D.current) {
                return;
            }

            pendingSync3D.current = false;
            sync3DData();
            map3d.current?.triggerRepaint();
        });
    }

    // ── 3D Map (Mapbox GL JS) ──────────────────────────────────────────────

    /**
     * Builds a GeoJSON FeatureCollection of visible paths (incl. snowmobile)
     */
    const buildPathsGeoJSON = (): any => {
        const features: any[] = [];
        if (!pathData.current || !pathGpsData.current) {
            debugLog('[3D] buildPathsGeoJSON: no pathData or pathGpsData', {
                hasPathData: !!pathData.current,
                pathDataKeys: pathData.current ? Object.keys(pathData.current).length : 0,
                hasGpsData: !!pathGpsData.current,
                gpsDataKeys: pathGpsData.current ? Object.keys(pathGpsData.current).length : 0,
            });
            return { type: 'FeatureCollection', features };
        }

        // When no filters are active, show ALL paths (same as 2D redraw)
        const noFilters = !filterData.current?.length;

        // Determine filter
        let pathIds: string[] | null = null;
        let snowSubIds: string[] | null = null;
        let hasPathFilter = noFilters; // true when no filters = show all
        let hasSnowFilter = noFilters;

        if (!noFilters) {
            const pf = filterData.current!.find(f => f.type === FeatureType.Path);
            hasPathFilter = !!pf;
            pathIds = pf?.ids ?? null;

            const sf = filterData.current!.find(f =>
                f.type === FeatureType.SnowmobilePath || f.type === FeatureType.SnowmobileSubPath
            );
            hasSnowFilter = !!sf;
            if (sf?.type === FeatureType.SnowmobilePath) {
                snowSubIds = [];
                sf.ids.forEach(id => {
                    const sp = snowmobilePathData.current[id];
                    sp?.subPaths?.forEach(sub => snowSubIds!.push(typeof sub === 'string' ? sub : sub.id));
                });
            } else if (sf) {
                snowSubIds = sf.ids;
            }
        }

        const hasHighlightedSelection = highlightedType.current !== undefined && highlightedId.current !== undefined;
        const highlightedPathIds = new Set<string>();
        const highlightedSnowSubPathIds = new Set<string>();
        const renderScale = getRenderScale();
        const baseInnerWidth = 6 * renderScale;
        const baseOutlineWidth = (6 + 8) * renderScale;
        const highlightedOutlineWidth = (6 + 16) * renderScale;

        if (highlightedType.current === FeatureType.Path && highlightedId.current) {
            const highlightedPath = pathData.current[`${highlightedId.current}`] as any;
            highlightedPathIds.add(`${highlightedId.current}`);

            (highlightedPath?.compositeChildPathIds ?? []).forEach((childId: number | string) => {
                highlightedPathIds.add(`${childId}`);
            });
        }

        if (highlightedType.current === FeatureType.SnowmobileSubPath && highlightedId.current) {
            highlightedSnowSubPathIds.add(`${highlightedId.current}`);
        }

        // Regular paths
        if (hasPathFilter) {
            getVisiblePaths(
                Object.values(pathData.current),
                pathGpsData.current,
                pathIds ?? undefined,
                highlightedType.current === FeatureType.Path ? highlightedId.current : undefined,
            ).forEach(path => {
                const coords = pathGpsData.current[path.id];
                if (!coords || coords.length === 0) return;

                const baseOpacity = path.status === PathStatus.Closed ? 0.5 : 1;
                const isHighlightedPath = highlightedPathIds.has(`${path.id}`);
                const opacity = hasHighlightedSelection
                    ? (isHighlightedPath ? baseOpacity : 0.35)
                    : baseOpacity;

                features.push({
                    type: 'Feature',
                    properties: {
                        id: path.id,
                        featureType: FeatureType.Path,
                        color: COLORS.PATHS.LINE,
                        opacity,
                        lineWidth: baseInnerWidth,
                        outlineWidth: isHighlightedPath ? highlightedOutlineWidth : baseOutlineWidth,
                        iconOpacity: hasHighlightedSelection
                            ? (isHighlightedPath ? (path.status === PathStatus.Closed ? 0.6 : 1) : 0.35)
                            : (path.status === PathStatus.Closed ? 0.6 : 1),
                    },
                    geometry: {
                        type: 'LineString',
                        coordinates: coords.map((c: any) => [c.longitude, c.latitude]),
                    }
                });
            });
        }

        // Snowmobile sub-paths
        if (hasSnowFilter && snowmobileSubPathData.current) {
            Object.values(snowmobileSubPathData.current).forEach(sub => {
                if (snowSubIds && snowSubIds.length && !snowSubIds.includes(sub.id)) return;
                const coords = sub.subPathGPSCoordinates;
                if (!coords || coords.length === 0) return;

                const baseOpacity = sub.status === PathStatus.Closed ? 0.5 : 1;
                const isHighlightedSubPath = highlightedSnowSubPathIds.has(`${sub.id}`);
                const opacity = hasHighlightedSelection
                    ? (isHighlightedSubPath ? baseOpacity : 0.35)
                    : baseOpacity;

                features.push({
                    type: 'Feature',
                    properties: {
                        id: sub.id,
                        featureType: FeatureType.SnowmobileSubPath,
                        color: COLORS.PATHS.LINE,
                        opacity,
                        lineWidth: baseInnerWidth,
                        outlineWidth: isHighlightedSubPath ? highlightedOutlineWidth : baseOutlineWidth,
                    },
                    geometry: {
                        type: 'LineString',
                        coordinates: coords.map((c: any) => [c.longitude, c.latitude]),
                    }
                });
            });
        }

        return { type: 'FeatureCollection', features };
    };

    /**
     * Builds a GeoJSON FeatureCollection of visible POIs
     */
    const buildPoisGeoJSON = (): any => {
        const features: any[] = [];
        const noClusterFeatures: any[] = [];
        if (!poiData.current) return { type: 'FeatureCollection', features, noClusterFeatures };

        const hasHighlightedSelection = highlightedType.current !== undefined && highlightedId.current !== undefined;
        const highlightedPoiId = highlightedType.current === FeatureType.Poi ? `${highlightedId.current}` : undefined;

        const noFilters = !filterData.current?.length;
        let poiIds: string[] | null = null;
        let hasFilter = noFilters; // true when no filters = show all
        if (!noFilters) {
            const pf = filterData.current!.find(f => f.type === FeatureType.Poi);
            hasFilter = !!pf;
            poiIds = pf?.ids ?? null;
        }
        if (!hasFilter) return { type: 'FeatureCollection', features, noClusterFeatures };

        // Group linked events under their POI exactly like the 2D map does: events sharing
        // their POI's position are not drawn on their own; the next upcoming/ongoing ones
        // are shown as a row below the POI (zoomed in) or as a count badge (zoomed out).
        const visiblePois = Object.values(poiData.current).filter((poi) => !poiIds?.length || poiIds.includes(poi.id));
        const { groups, groupedEventIds } = groupLinkedEvents(visiblePois);
        debugLog('[3D] buildPoisGeoJSON: total pois:', Object.keys(poiData.current).length,
            ', visible:', visiblePois.length,
            ', events:', visiblePois.filter(p => (p as any).sourceType === 'event').length,
            ', grouped:', groupedEventIds.size,
            ', groups:', Object.keys(groups).length);
        // Debug: log all events and their linked POI ids
        visiblePois.filter(p => (p as any).sourceType === 'event').forEach(p => {
            debugLog('[3D] event:', p.id, 'linkedPoiId:', (p as any).linkedPoiId, 'pointOfInterestId:', (p as any).pointOfInterestId, 'pos:', p.longitude, p.latitude);
        });

        Object.values(poiData.current).forEach(poi => {
            if (poiIds && poiIds.length && !poiIds.includes(poi.id)) return;
            // Events grouped under their POI are presented via the group's row/badge features
            if (groupedEventIds.has(`${poi.id}`)) return;
            const color = poiCategoryProps.current?.[poi.poiCategoryId]?.color || '#666666';
            const isHighlightedPoi = highlightedPoiId === `${poi.id}`;
            const iconOpacity = hasHighlightedSelection ? (isHighlightedPoi ? 1 : 0.35) : 1;
            // Events and temperature POIs are excluded from clustering (same as the 2D map's
            // geometryFunction which returns null for them).
            const isEvent = (poi as any).sourceType === 'event' || `${poi.id}`.startsWith('event-');
            const isTemperature = poi.poiCategoryId === '19';
            const feature = {
                type: 'Feature',
                properties: {
                    id: poi.id,
                    featureType: FeatureType.Poi,
                    name: getTranslatedProperty(selectedLanguage.current, 'name', poi),
                    color,
                    iconImage: `poi-cat-${poi.poiCategoryId}`,
                    iconOpacity,
                },
                geometry: {
                    type: 'Point',
                    coordinates: [poi.longitude, poi.latitude],
                }
            };
            (isEvent || isTemperature ? noClusterFeatures : features).push(feature);
        });

        // Event rows below their POI (shown when zoomed in, same as 2D's POI_TEXT_MIN_ZOOM).
        // Instead of icon-offset (which doesn't work reliably), we shift the event's coordinates
        // southward by a fixed amount, matching the 2D layout of 140px below the POI.
        Object.entries(groups).forEach(([poiId, events]) => {
            const poi = poiData.current[poiId];
            if (!poi || !isValidLonLat(poi.longitude, poi.latitude)) return;
            const coordinates: [number, number] = [Number(poi.longitude), Number(poi.latitude)];
            const color = poiCategoryProps.current?.[poi.poiCategoryId]?.color || '#666666';

            events.forEach((event, index) => {
                const categoryProps = poiCategoryProps.current?.[event.poiCategoryId];
                if (!categoryProps) {
                    debugLog('[3D] buildPoisGeoJSON: no categoryProps for event', event.id, 'cat:', event.poiCategoryId);
                    return;
                }
                const name = truncateLabel(getTranslatedProperty(selectedLanguage.current, 'name', event) ?? '');
                const date = formatEventDate((event as any).startAt);
                const label = [name, date].filter(Boolean).join('\n');
                // Shift event south by ~140px at zoom 15 (approx 0.0003 degrees latitude)
                // and east by 75px + index*130px (approx 0.0002 + index*0.00035 degrees longitude)
                const shiftedLat = coordinates[1] - 0.0003;
                const shiftedLng = coordinates[0] + 0.0002 + index * 0.00035;
                debugLog('[3D] buildPoisGeoJSON: event row', event.id, 'idx:', index, 'label:', label, 'icon:', `poi-cat-${event.poiCategoryId}`, 'shifted:', shiftedLng, shiftedLat);
                noClusterFeatures.push({
                    type: 'Feature',
                    properties: {
                        id: event.id,
                        featureType: FeatureType.Poi,
                        name: label,
                        color: categoryProps.color,
                        iconImage: `poi-cat-${event.poiCategoryId}`,
                        iconOpacity: 1,
                        eventRowIndex: index,
                    },
                    geometry: { type: 'Point', coordinates: [shiftedLng, shiftedLat] },
                });
            });

            // Count badge on the POI, shown when zoomed out (same as 2D).
            // Use icon-offset (not coordinate shift) so the offset is zoom-independent.
            const count = Math.min(events.length, 9);
            noClusterFeatures.push({
                type: 'Feature',
                properties: {
                    id: poi.id,
                    featureType: FeatureType.Poi,
                    name: `${events.length}`,
                    color,
                    iconImage: `poi-event-badge-${poi.poiCategoryId}-${count}`,
                    iconOpacity: 1,
                    isEventBadge: true,
                },
                geometry: { type: 'Point', coordinates },
            });
        });

        return { type: 'FeatureCollection', features, noClusterFeatures };
    };

    /**
     * Builds a GeoJSON FeatureCollection of visible AOIs
     */
    const buildAoisGeoJSON = (): any => {
        const features: any[] = [];
        if (!aoiData.current || !aoiGpsData.current) return { type: 'FeatureCollection', features };

        const hasHighlightedSelection = highlightedType.current !== undefined && highlightedId.current !== undefined;
        const highlightedAoiId = highlightedType.current === FeatureType.Aoi ? `${highlightedId.current}` : undefined;

        const noFilters = !filterData.current?.length;
        let aoiIds: string[] | null = null;
        let hasFilter = noFilters; // true when no filters = show all
        if (!noFilters) {
            const af = filterData.current!.find(f => f.type === FeatureType.Aoi);
            hasFilter = !!af;
            aoiIds = af?.ids ?? null;
        }
        if (!hasFilter) return { type: 'FeatureCollection', features };

        Object.values(aoiData.current).forEach(aoi => {
            if (aoiIds && aoiIds.length && !aoiIds.includes(aoi.id)) return;
            const coords = aoiGpsData.current[aoi.id];
            if (!coords || coords.length === 0) return;
            const color = aoiCategoryProps.current?.[aoi.aoiCategoryId]?.color || '#666666';
            const isHighlightedAoi = highlightedAoiId === `${aoi.id}`;
            const fillOpacity = hasHighlightedSelection ? (isHighlightedAoi ? 0.3 : 0.12) : 0.3;
            const lineOpacity = hasHighlightedSelection ? (isHighlightedAoi ? 1 : 0.35) : 1;
            const ring = coords.map((c: any) => [c.longitude, c.latitude]);
            // Close ring
            if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
                ring.push([...ring[0]]);
            }
            features.push({
                type: 'Feature',
                properties: {
                    id: aoi.id,
                    featureType: FeatureType.Aoi,
                    color,
                    name: getTranslatedProperty(selectedLanguage.current, 'name', aoi),
                    fillOpacity,
                    lineOpacity,
                },
                geometry: {
                    type: 'Polygon',
                    coordinates: [ring],
                }
            });
        });
        return { type: 'FeatureCollection', features };
    };

    /**
     * Destroys the 3D Mapbox GL map instance completely
     */
    const destroy3DMap = () => {
        debugLog('[3D] destroy3DMap called v2');
        if (map3d.current) {
            try { map3d.current.remove(); } catch (e) { /* ignore */ }
            map3d.current = null;
            map3dLoaded.current = false;
        }
    };

    /**
     * Generates a combined backplate + icon canvas for a path category icon.
     * Matches the 2D map's rounded-rect style (white fill, colored border, tinted icon).
     */
    const create3DPathIconImage = (iconImg: HTMLImageElement, color: string, size: number = 48): ImageData => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;

        const borderWidth = Math.max(2, size * 0.06);
        const radius = size * 0.30;
        const inset = borderWidth / 2;

        // Draw rounded rect backplate
        ctx.beginPath();
        ctx.moveTo(inset + radius, inset);
        ctx.lineTo(size - inset - radius, inset);
        ctx.arcTo(size - inset, inset, size - inset, inset + radius, radius);
        ctx.lineTo(size - inset, size - inset - radius);
        ctx.arcTo(size - inset, size - inset, size - inset - radius, size - inset, radius);
        ctx.lineTo(inset + radius, size - inset);
        ctx.arcTo(inset, size - inset, inset, size - inset - radius, radius);
        ctx.lineTo(inset, inset + radius);
        ctx.arcTo(inset, inset, inset + radius, inset, radius);
        ctx.closePath();

        // White fill
        ctx.fillStyle = 'white';
        ctx.fill();

        // Colored border
        ctx.strokeStyle = color;
        ctx.lineWidth = borderWidth;
        ctx.stroke();

        // Draw the icon centered, scaled to fit inside backplate
        const iconPad = size * 0.2;
        const iconArea = size - iconPad * 2;
        const aspect = iconImg.width / iconImg.height;
        let drawW, drawH;
        if (aspect > 1) {
            drawW = iconArea;
            drawH = iconArea / aspect;
        } else {
            drawH = iconArea;
            drawW = iconArea * aspect;
        }
        const drawX = (size - drawW) / 2;
        const drawY = (size - drawH) / 2;

        // Tint the icon with the path color
        // First draw icon to offscreen canvas, then composite with color
        const tintCanvas = document.createElement('canvas');
        tintCanvas.width = size;
        tintCanvas.height = size;
        const tCtx = tintCanvas.getContext('2d')!;
        tCtx.drawImage(iconImg, drawX, drawY, drawW, drawH);
        tCtx.globalCompositeOperation = 'source-in';
        tCtx.fillStyle = color;
        tCtx.fillRect(0, 0, size, size);

        ctx.drawImage(tintCanvas, 0, 0);

        return ctx.getImageData(0, 0, size, size);
    };

    /**
     * Generates a combined white-circle + icon canvas for a POI category icon.
     * Matches the 2D map's circle backplate style.
     */
    const create3DPoiIconImage = (
        iconImg: HTMLImageElement,
        color: string,
        size: number = 48,
        tintIcon: boolean = true,
    ): ImageData => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;

        // White circle backplate (matches 2D map's backplate style)
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.fill();

        // Draw the icon centered — icon fills ~85% of circle (matching 2D ratio)
        const iconPad = size * 0.10;
        const iconArea = size - iconPad * 2;
        const aspect = iconImg.width / iconImg.height;
        let drawW, drawH;
        if (aspect > 1) {
            drawW = iconArea;
            drawH = iconArea / aspect;
        } else {
            drawH = iconArea;
            drawW = iconArea * aspect;
        }
        const drawX = (size - drawW) / 2;
        const drawY = (size - drawH) / 2;

        if (tintIcon) {
            const tintCanvas = document.createElement('canvas');
            tintCanvas.width = size;
            tintCanvas.height = size;
            const tCtx = tintCanvas.getContext('2d')!;
            tCtx.drawImage(iconImg, drawX, drawY, drawW, drawH);
            tCtx.globalCompositeOperation = 'source-in';
            tCtx.fillStyle = color;
            tCtx.fillRect(0, 0, size, size);

            ctx.drawImage(tintCanvas, 0, 0);
        } else {
            ctx.drawImage(iconImg, drawX, drawY, drawW, drawH);
        }

        return ctx.getImageData(0, 0, size, size);
    };

    /**
     * Loads all POI category icons into a Mapbox GL map as images.
     * Returns a promise that resolves when all icons are loaded.
     */
    const load3DPoiIcons = (m: mapboxgl.Map): Promise<void> => {
        const iconDefs: { [key: string]: { url: string, color: string, tintIcon: boolean } } = {};

        // Build from poiCategoryProps (same mapping as 2D)
        if (poiCategoryProps.current) {
            for (const [catId, props] of Object.entries(poiCategoryProps.current)) {
                const { icon, color } = props as { icon: string; color: string; closedIcon: string };
                if (icon) {
                    iconDefs[`poi-cat-${catId}`] = {
                        url: icon,
                        color,
                        tintIcon: icon.toLowerCase().endsWith('.png'),
                    };
                }
            }
        }
        debugLog('[3D] load3DPoiIcons: categories:', Object.keys(poiCategoryProps.current || {}), 'iconDefs:', Object.keys(iconDefs));

        const loadPromises = Object.entries(iconDefs).map(([imgId, { url, color, tintIcon }]) => {
            return new Promise<void>((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    try {
                        if (!m.hasImage(imgId)) {
                            const imageData = create3DPoiIconImage(img, color, 64, tintIcon);
                            m.addImage(imgId, imageData, { pixelRatio: 2 });
                            debugLog('[3D] Added POI icon:', imgId);
                        }
                    } catch (e) {
                        console.warn('[3D] Failed to add POI icon image:', imgId, e);
                    }
                    resolve();
                };
                img.onerror = () => {
                    console.warn('[3D] Failed to load POI icon:', imgId, 'url:', url?.substring(0, 100));
                    resolve();
                };
                img.src = url;
            });
        });

        return Promise.all(loadPromises).then(() => {
            // Badge icons with count baked in (1-9) per POI category color
            if (poiCategoryProps.current) {
                for (const [catId, props] of Object.entries(poiCategoryProps.current)) {
                    const color = (props as { color?: string }).color || '#666666';
                    for (let count = 1; count <= 9; count++) {
                        const imgId = `poi-event-badge-${catId}-${count}`;
                        if (!m.hasImage(imgId)) {
                            const imageData = create3DBadgeImage(color, count);
                            m.addImage(imgId, imageData, { pixelRatio: 2 });
                            debugLog('[3D] Added badge icon:', imgId);
                        }
                    }
                }
            }
            debugLog('[3D] All POI icons loaded:', Object.keys(iconDefs).length);
        });
    };

    /**
     * Creates a badge image with the event count baked in, offset within the canvas
     * so it appears on the POI edge without needing icon-offset.
     */
    const create3DBadgeImage = (color: string, count: number): ImageData => {
        const size = 64; // Larger canvas to accommodate offset
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;

        // Draw circle offset to bottom-right (matches 2D displacement of [22, 22] at scale 0.5)
        const offset = 22;
        const radius = 15;
        const centerX = size / 2 + offset;
        const centerY = size / 2 + offset;

        // Colored circle with white border (matches 2D)
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 4;
        ctx.stroke();

        // White count text centered in the circle
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${count}`, centerX, centerY);

        return ctx.getImageData(0, 0, size, size);
    };

    /**
     * Loads all path category icons into a Mapbox GL map as images.
     * Returns a promise that resolves when all icons are loaded.
     */
    const load3DPathIcons = (m: mapboxgl.Map): Promise<void> => {
        // Map of icon image ID -> { url, color }
        const iconDefs: { [key: string]: { url: string, color: string } } = {};

        // Build from PROPS (same mapping as 2D map)
        if (pathCategoryProps.current) {
            for (const [catId, props] of Object.entries(pathCategoryProps.current)) {
                const { icon, color } = props as { icon: string; color: string; closedIcon: string };
                if (icon) {
                    iconDefs[`path-cat-${catId}`] = { url: icon, color };
                }
            }
        }

        // Fallback: define for all known categories from ICONS constants
        const fallbackDefs: { [key: string]: { url: string, color: string } } = {
            [`path-cat-${PathCategoryId.CrossCountrySkiing}`]: { url: ICONS.PATHS.CROSS_COUNTRY_SKIING, color: COLORS.PATHS.CROSS_COUNTRY_SKIING },
            [`path-cat-${PathCategoryId.Snowmobiling}`]:      { url: ICONS.PATHS.SNOWMOBILING, color: COLORS.PATHS.SNOWMOBILING },
            [`path-cat-${PathCategoryId.MountainBiking}`]:    { url: ICONS.PATHS.MOUNTAIN_BIKING, color: COLORS.PATHS.MOUNTAIN_BIKING },
            [`path-cat-${PathCategoryId.Hiking}`]:            { url: ICONS.PATHS.HIKING, color: COLORS.PATHS.HIKING },
            [`path-cat-${PathCategoryId.Running}`]:           { url: ICONS.PATHS.RUNNING, color: COLORS.PATHS.RUNNING },
            [`path-cat-${PathCategoryId.Riding}`]:            { url: ICONS.PATHS.RIDING, color: COLORS.PATHS.RIDING },
            [`path-cat-${PathCategoryId.Canoe}`]:             { url: ICONS.PATHS.CANOE, color: COLORS.PATHS.CANOE },
            [`path-cat-${PathCategoryId.ChairLift}`]:         { url: ICONS.PATHS.CHAIR_LIFT, color: COLORS.PATHS.CHAIR_LIFT },
            [`path-cat-${PathCategoryId.Gondola}`]:           { url: ICONS.PATHS.GONDOLA, color: COLORS.PATHS.GONDOLA },
            [`path-cat-${PathCategoryId.SurfaceLift}`]:       { url: ICONS.PATHS.SURFACE_LIFT, color: COLORS.PATHS.SURFACE_LIFT },
            [`path-cat-${PathCategoryId.BeltLift}`]:          { url: ICONS.PATHS.BELT_LIFT, color: COLORS.PATHS.BELT_LIFT },
            [`path-cat-${PathCategoryId.SkiSlope}`]:          { url: ICONS.PATHS.SKI_SLOPE, color: COLORS.PATHS.SKI_SLOPE },
        };

        // Merge fallbacks for any missing categories
        for (const [key, def] of Object.entries(fallbackDefs)) {
            if (!iconDefs[key]) iconDefs[key] = def;
        }

        const loadPromises = Object.entries(iconDefs).map(([imgId, { url, color }]) => {
            return new Promise<void>((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    try {
                        if (!m.hasImage(imgId)) {
                            const imageData = create3DPathIconImage(img, color, 64);
                            m.addImage(imgId, imageData, { pixelRatio: 2 });
                        }
                    } catch (e) {
                        console.warn('[3D] Failed to add icon image:', imgId, e);
                    }
                    resolve();
                };
                img.onerror = () => {
                    console.warn('[3D] Failed to load icon:', url);
                    resolve(); // Don't block on failure
                };
                img.src = url;
            });
        });

        return Promise.all(loadPromises).then(() => {
            debugLog('[3D] All path icons loaded:', Object.keys(iconDefs).length);
        });
    };

    /**
     * Initializes the Mapbox GL 3D map with terrain (always creates fresh)
     */
    const init3DMap = (type: MapType = MapType.ThreeD) => {
        if (!map3dRef.current) return;

        // Get current OL view center and zoom to sync
        const view = map.current?.getView();
        const olCenter = view ? toLonLat(view.getCenter()!) : [
            parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || '20.2630'),
            parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || '63.8258')
        ];
        const olZoom = view?.getZoom() || parseInt(process.env.REACT_APP_MAP_DEFAULT_ZOOM || '14', 10);

        // Destroy previous instance if it exists
        destroy3DMap();

        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

        const styleUrl = type === MapType.ThreeDSatelite
            ? `mapbox://styles/${MAPBOX_3D_SATELLITE_STYLE_USER}/${MAPBOX_3D_SATELLITE_STYLE_ID}`
            : `mapbox://styles/${MAPBOX_3D_STYLE_USER}/${MAPBOX_3D_STYLE_ID}`;

        map3d.current = new mapboxgl.Map({
            container: map3dRef.current,
            style: styleUrl,
            center: [olCenter[0], olCenter[1]],
            zoom: olZoom,
            pitch: 60,
            bearing: -17,
            antialias: true,
            maxZoom: 20,
            minPitch: 0,
            maxPitch: 85,
        });

        map3d.current.addControl(new mapboxgl.NavigationControl({ visualizePitch: true, showZoom: false }), 'bottom-left');

        // Tune drag interaction for smoother 3D terrain navigation
        // Reduce inertia so the map doesn't "jump" when releasing a drag
        if (map3d.current.dragPan) {
            map3d.current.dragPan.enable({
                linearity: 0.15,
                maxSpeed: 1000,
                deceleration: 3000,
            } as any);
        }

        // Tune touch zoom/rotate for 3D terrain navigation
        if (map3d.current.touchZoomRotate) {
            // Rotation enabled for 3D navigation
        }

        // Tune touch pitch for smoother tilt gestures
        if (map3d.current.touchPitch) {
            map3d.current.touchPitch.enable({
                around: 'center',
            } as any);
        }

        const onMapLoad = () => {
          try {
            const m = map3d.current!;
            if (map3dLoaded.current) return; // already handled
            map3dLoaded.current = true;
            debugLog('[3D] Mapbox GL map loaded, adding terrain + syncing data');

            // Load POI icons on demand if they weren't ready when layers were added
            m.on('styleimagemissing', (e: any) => {
                const id = e.id;
                if (!id?.startsWith('poi-cat-')) return;
                const catId = id.replace('poi-cat-', '');
                const props = poiCategoryProps.current?.[catId] as { icon: string; color: string } | undefined;
                if (!props?.icon) return;
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    try {
                        if (!m.hasImage(id)) {
                            const imageData = create3DPoiIconImage(img, props.color, 64, props.icon.toLowerCase().endsWith('.png'));
                            m.addImage(id, imageData, { pixelRatio: 2 });
                        }
                    } catch (err) {
                        console.warn('[3D] Failed to add missing POI icon:', id, err);
                    }
                };
                img.onerror = () => console.warn('[3D] Failed to load missing POI icon:', props.icon);
                img.src = props.icon;
            });
            // If filters/selection arrived before 3D finished loading, run refocus again now.
            setTimeout(() => {
                try {
                    if (selectedType.current || filterData.current?.length) {
                        refocus();
                    }
                } catch (error) {
                    console.warn('[3D] deferred refocus failed:', error);
                }
            }, 0);
            debugLog('[3D] filterData:', JSON.stringify(filterData.current));
            debugLog('[3D] pathData keys:', pathData.current ? Object.keys(pathData.current).length : 0);
            debugLog('[3D] pathGpsData keys:', pathGpsData.current ? Object.keys(pathGpsData.current).length : 0);

            // Helper: safely add source/layer only if not already present
            const safeAddSource = (id: string, spec: any) => {
                if (!m.getSource(id)) m.addSource(id, spec);
            };
            const safeAddLayer = (spec: any) => {
                if (!m.getLayer(spec.id)) m.addLayer(spec);
            };

            // Add terrain DEM source
            safeAddSource('mapbox-dem', {
                type: 'raster-dem',
                url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                tileSize: 512,
                maxzoom: 14,
            });
            m.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });

            // Add sky atmosphere
            safeAddLayer({
                id: 'sky',
                type: 'sky',
                paint: {
                    'sky-type': 'atmosphere',
                    'sky-atmosphere-sun': [0.0, 90.0],
                    'sky-atmosphere-sun-intensity': 15,
                },
            });

            // Pre-create sources + layers WITH data so rendering starts immediately
            const initPathGeoJSON = buildPathsGeoJSON();
            const initAoiGeoJSON = buildAoisGeoJSON();
            const initPoiGeoJSON = buildPoisGeoJSON();
            debugLog('[3D] init data: paths=' + initPathGeoJSON.features.length +
                ', aois=' + initAoiGeoJSON.features.length +
                ', pois=' + initPoiGeoJSON.features.length +
                ', poisNoCluster=' + initPoiGeoJSON.noClusterFeatures.length);

            // Paths — white outline and blue line on top (same as 2D map)
            safeAddSource('paths-source', { type: 'geojson', data: initPathGeoJSON });
            // White outline (4 inner + 6 border = 10 total)
            safeAddLayer({
                id: 'paths-outline-layer',
                type: 'line',
                source: 'paths-source',
                paint: {
                    'line-color': '#FFFFFF',
                    'line-width': ['coalesce', ['get', 'outlineWidth'], 7],
                    'line-opacity': ['coalesce', ['get', 'opacity'], 1],
                },
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                }
            });
            // Blue path line on top
            safeAddLayer({
                id: 'paths-layer',
                type: 'line',
                source: 'paths-source',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': ['coalesce', ['get', 'lineWidth'], 3],
                    'line-opacity': ['coalesce', ['get', 'opacity'], 1],
                },
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                }
            });

            // Build path icon midpoints from initial path data
            const initPathIcons: any[] = [];
            initPathGeoJSON.features.forEach((f: any) => {
                if (f.geometry?.type === 'LineString' && f.geometry.coordinates?.length > 1) {
                    const coords = f.geometry.coordinates;
                    const midCoord = coords[Math.floor(coords.length / 2)];
                    const pathObj = pathData.current?.[f.properties.id];
                    const catId = pathObj?.pathCategoryId || PathCategoryId.Hiking;
                    initPathIcons.push({
                        type: 'Feature',
                        properties: {
                            id: f.properties.id,
                            featureType: f.properties.featureType,
                            color: f.properties.color,
                            iconImage: `path-cat-${catId}`,
                        },
                        geometry: { type: 'Point', coordinates: midCoord },
                    });
                }
            });

            // Path icon markers (midpoints) — load icons then add layers
            safeAddSource('path-icons-source', { type: 'geojson', data: { type: 'FeatureCollection', features: initPathIcons } });

            // Direction arrows for selected path
            safeAddSource('path-direction-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

            const directionArrowImg = new Image();
            directionArrowImg.crossOrigin = 'anonymous';
            directionArrowImg.onload = () => {
                try {
                    if (!m.hasImage('direction-arrow')) {
                        const canvas = document.createElement('canvas');
                        canvas.width = directionArrowImg.width;
                        canvas.height = directionArrowImg.height;
                        const ctx = canvas.getContext('2d')!;
                        ctx.drawImage(directionArrowImg, 0, 0);
                        const imageData = ctx.getImageData(0, 0, directionArrowImg.width, directionArrowImg.height);
                        m.addImage('direction-arrow', imageData, { pixelRatio: 2 });
                    }

                    safeAddLayer({
                        id: 'path-direction-layer',
                        type: 'symbol',
                        source: 'path-direction-source',
                        layout: {
                            'symbol-placement': 'line',
                            'symbol-spacing': ['interpolate', ['linear'], ['zoom'], 8, 12000, 10, 9000, 12, 6000, 14, 4000, 16, 2500, 18, 1500, 20, 900],
                            'icon-image': 'direction-arrow',
                            'icon-size': 0.8,
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                            'icon-rotation-alignment': 'map',
                            'icon-keep-upright': false,
                        },
                        minzoom: 13,
                    });
                    m.triggerRepaint();
                } catch (e) {
                    console.warn('[3D] Failed to add direction-arrow image/layer:', e);
                }
            };
            directionArrowImg.onerror = () => {
                console.warn('[3D] Failed to load direction arrow icon from:', ICONS.DIRECTION_ARROW);
            };
            directionArrowImg.src = ICONS.DIRECTION_ARROW;

            // Load actual PNG icons (same as 2D map) then add symbol layer
            load3DPathIcons(m).then(() => {
                safeAddLayer({
                    id: 'path-icons-layer',
                    type: 'symbol',
                    source: 'path-icons-source',
                    layout: {
                        'icon-image': ['get', 'iconImage'],
                        'icon-size': 0.85,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                    },
                    paint: {
                        'icon-opacity': ['coalesce', ['get', 'iconOpacity'], 1],
                    },
                    minzoom: 13,
                });
                m.triggerRepaint();
            });

            // AOIs
            safeAddSource('aois-source', { type: 'geojson', data: initAoiGeoJSON });
            safeAddLayer({
                id: 'aois-fill-layer',
                type: 'fill',
                source: 'aois-source',
                paint: {
                    'fill-color': ['get', 'color'],
                    'fill-opacity': ['coalesce', ['get', 'fillOpacity'], 0.3],
                }
            });
            safeAddLayer({
                id: 'aois-outline-layer',
                type: 'line',
                source: 'aois-source',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 2,
                    'line-opacity': ['coalesce', ['get', 'lineOpacity'], 1],
                }
            });

            // POIs — clustered source (same behaviour as the 2D map's Cluster source)
            safeAddSource('pois-source', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: initPoiGeoJSON.features },
                cluster: true,
                clusterRadius: 60,
                clusterMaxZoom: 13,
            });
            // Events and temperature POIs never cluster (same as 2D)
            safeAddSource('pois-nocluster-source', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: initPoiGeoJSON.noClusterFeatures },
            });
            // Separate source for event badges so they render on top of POIs
            safeAddSource('poi-event-badge-source', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
            });

            load3DPoiIcons(m).then(() => {
                // Cluster circles (blue circle, radius grows with digit count – same as 2D)
                safeAddLayer({
                    id: 'poi-clusters-layer',
                    type: 'circle',
                    source: 'pois-source',
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': COLORS.POIS.DEFAULT || '#19456B',
                        'circle-radius': [
                            'step',
                            ['get', 'point_count'],
                            12,   // 1-9
                            10, 14,  // 10-99
                            100, 16, // 100-999
                            1000, 18 // 1000+
                        ],
                        'circle-stroke-color': '#FFFFFF',
                        'circle-stroke-width': 2,
                    },
                });

                // Cluster count (white, centred by Mapbox)
                safeAddLayer({
                    id: 'poi-cluster-count-layer',
                    type: 'symbol',
                    source: 'pois-source',
                    filter: ['has', 'point_count'],
                    layout: {
                        'text-field': ['get', 'point_count_abbreviated'],
                        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                        'text-size': 14,
                        'text-anchor': 'center',
                        'text-justify': 'center',
                        'text-allow-overlap': true,
                        'text-ignore-placement': true,
                    },
                    paint: {
                        'text-color': '#FFFFFF',
                    },
                });

                // Individual (unclustered) POIs
                safeAddLayer({
                    id: 'pois-layer',
                    type: 'symbol',
                    source: 'pois-source',
                    filter: ['!', ['has', 'point_count']],
                    layout: {
                        'icon-image': ['get', 'iconImage'],
                        'icon-size': 0.85,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                        'text-field': ['get', 'name'],
                        'text-size': 12,
                        'text-offset': [0, 1.8],
                        'text-anchor': 'top',
                        'text-optional': true,
                    },
                    paint: {
                        'icon-opacity': ['coalesce', ['get', 'iconOpacity'], 1],
                        'text-opacity': ['coalesce', ['get', 'iconOpacity'], 1],
                        'text-color': '#333',
                        'text-halo-color': 'white',
                        'text-halo-width': 1,
                    },
                });

                // Events and temperature POIs (never clustered, same as 2D).
                // Event row icons are offset to the right of their POI, matching the 2D layout
                // (POI_EVENT_ROW_FIRST_X/SPACING/OFFSET_Y in px, converted to ems at icon-size 0.85).
                // Row icons are only shown when zoomed in (same as 2D's POI_TEXT_MIN_ZOOM = 15).
                safeAddLayer({
                    id: 'pois-nocluster-layer',
                    type: 'symbol',
                    source: 'pois-nocluster-source',
                    filter: [
                        'all',
                        ['!', ['==', ['get', 'isEventBadge'], true]],
                        ['!', ['has', 'eventRowIndex']],
                    ],
                    layout: {
                        'icon-image': ['get', 'iconImage'],
                        'icon-size': 0.85,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                        'text-field': ['get', 'name'],
                        'text-size': 12,
                        'text-offset': ['literal', [0, 1.8]],
                        'text-anchor': 'top',
                        'text-optional': true,
                    },
                    paint: {
                        'icon-opacity': ['coalesce', ['get', 'iconOpacity'], 1],
                        'text-opacity': ['coalesce', ['get', 'iconOpacity'], 1],
                        'text-color': '#333',
                        'text-halo-color': 'white',
                        'text-halo-width': 1,
                    },
                    minzoom: 11,
                });

                // Event row icons below their POI, shown when zoomed in (same as 2D).
                // Coordinates are already shifted southward in buildPoisGeoJSON.
                safeAddLayer({
                    id: 'poi-event-row-layer',
                    type: 'symbol',
                    source: 'pois-nocluster-source',
                    filter: ['all', ['has', 'eventRowIndex'], ['!', ['==', ['get', 'isEventBadge'], true]]],
                    layout: {
                        'icon-image': ['get', 'iconImage'],
                        'icon-size': 0.85,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                        'text-field': ['get', 'name'],
                        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                        'text-size': 12,
                        'text-offset': [0, 1.8],
                        'text-anchor': 'top',
                        'text-optional': true,
                    },
                    paint: {
                        'icon-opacity': ['coalesce', ['get', 'iconOpacity'], 1],
                        'text-opacity': ['coalesce', ['get', 'iconOpacity'], 1],
                        'text-color': '#333',
                        'text-halo-color': 'white',
                        'text-halo-width': 1,
                    },
                    minzoom: 15,
                });

                // Event count badge on POIs, shown when zoomed out (same as 2D).
                // Badge image has the offset baked in, so no icon-offset needed.
                // Uses a separate source so it renders on top of the POI layer.
                safeAddLayer({
                    id: 'poi-event-badge-layer',
                    type: 'symbol',
                    source: 'poi-event-badge-source',
                    filter: ['==', ['get', 'isEventBadge'], true],
                    layout: {
                        'icon-image': ['get', 'iconImage'],
                        'icon-size': 1,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                    },
                    maxzoom: 15,
                });
                m.triggerRepaint();
                debugLog('[3D] Layers added:', m.getStyle().layers.map((l: any) => l.id).filter((id: string) => id.includes('poi')));
            });

            // GPS — load same icon as 2D map (gps-position.png) with heading rotation
            safeAddSource('gps-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

            // Load GPS icon via HTML Image (works with relative paths in WebView)
            const gpsImg = new Image();
            gpsImg.crossOrigin = 'anonymous';
            gpsImg.onload = () => {
                try {
                    if (!m.hasImage('gps-icon')) {
                        const canvas = document.createElement('canvas');
                        canvas.width = gpsImg.width;
                        canvas.height = gpsImg.height;
                        const ctx = canvas.getContext('2d')!;
                        ctx.drawImage(gpsImg, 0, 0);
                        const imageData = ctx.getImageData(0, 0, gpsImg.width, gpsImg.height);
                        m.addImage('gps-icon', imageData, { pixelRatio: 2 });
                    }
                    safeAddLayer({
                        id: 'gps-layer',
                        type: 'symbol',
                        source: 'gps-source',
                        layout: {
                            'icon-image': 'gps-icon',
                            'icon-size': 1.1,
                            'icon-rotate': ['get', 'heading'],
                            'icon-rotation-alignment': 'map',
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                        },
                    });
                    m.triggerRepaint();
                } catch (e) {
                    console.warn('[3D] Failed to add GPS icon:', e);
                }
            };
            gpsImg.onerror = () => {
                console.warn('[3D] Failed to load GPS icon from:', ICONS.GPS_POSITION);
            };
            gpsImg.src = ICONS.GPS_POSITION;

            // Delayed re-syncs to ensure rendering after terrain settles
            setTimeout(() => { scheduleSync3DData(); }, 500);
            setTimeout(() => { scheduleSync3DData(); }, 2000);

            // Click on a cluster zooms into its expansion zoom level
            m.on('click', 'poi-clusters-layer', (e: any) => {
                const features = m.queryRenderedFeatures(e.point, { layers: ['poi-clusters-layer'] });
                const clusterId = features[0]?.properties?.cluster_id;
                if (clusterId === undefined) return;
                (m.getSource('pois-source') as mapboxgl.GeoJSONSource).getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err || zoom == null) return;
                    const geometry = features[0].geometry;
                    if (geometry.type !== 'Point') return;
                    m.easeTo({ center: geometry.coordinates as [number, number], zoom });
                });
            });

            // Change cursor to pointer over clusters
            m.on('mouseenter', 'poi-clusters-layer', () => { m.getCanvas().style.cursor = 'pointer'; });
            m.on('mouseleave', 'poi-clusters-layer', () => { m.getCanvas().style.cursor = ''; });

            // Unified click handler with bbox tolerance for easier line hits
            m.on('click', (e: any) => {
                // Skip if a cluster was clicked (handled by the cluster click handler above)
                const clusterFeatures = m.queryRenderedFeatures(e.point, { layers: ['poi-clusters-layer'] });
                if (clusterFeatures.length > 0) return;

                const tolerance = 10;
                const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
                    [e.point.x - tolerance, e.point.y - tolerance],
                    [e.point.x + tolerance, e.point.y + tolerance]
                ];
                const layers = ['paths-layer', 'paths-outline-layer', 'path-icons-layer', 'pois-layer', 'pois-nocluster-layer', 'poi-event-row-layer', 'poi-event-badge-layer', 'aois-fill-layer'];
                const features = m.queryRenderedFeatures(bbox, { layers });
                debugLog('[3D] click at', e.point, 'features found:', features.length);

                if (features.length > 0) {
                    const candidates = features.map((f: any) => {
                        const props = f.properties || {};
                        return {
                            type: props.featureType,
                            id: props.id !== undefined && props.id !== null ? String(props.id) : undefined,
                        };
                    });

                    const hit = pickPreferredCandidate(candidates);
                    debugLog('[3D] clicked feature:', hit);
                    if (hit?.id && hit?.type) {
                        const msg = JSON.stringify({ type: hit.type, id: hit.id });
                        postMessage(msg);
                        onMessage?.(msg);
                    }
                } else {
                    const msg = JSON.stringify({ deselect: true });
                    postMessage(msg);
                    onMessage?.(msg);
                }
            });

            // Drag invalidates GPS centered
            m.on('dragstart', () => {
                if (gpsIsCentered.current) {
                    const msg = JSON.stringify({ centered: false });
                    postMessage(msg);
                    onMessage?.(msg);
                    gpsIsCentered.current = false;
                }
            });

            // Cursor hints
            ['paths-layer', 'paths-outline-layer', 'path-icons-layer', 'pois-layer', 'pois-nocluster-layer', 'poi-event-row-layer', 'poi-event-badge-layer', 'aois-fill-layer'].forEach(layer => {
                m.on('mouseenter', layer, () => { m.getCanvas().style.cursor = 'pointer'; });
                m.on('mouseleave', layer, () => { m.getCanvas().style.cursor = ''; });
            });

          } catch (err: any) {
            console.error('[3D] load handler error:', err);
            // Show error visually since we can't access WebView console
            const errDiv = document.createElement('div');
            errDiv.style.cssText = 'position:fixed;top:60px;left:10px;right:10px;background:red;color:white;padding:12px;border-radius:6px;font:13px monospace;z-index:99999;word-wrap:break-word;';
            errDiv.textContent = `3D ERROR: ${err?.message || err}`;
            document.body.appendChild(errDiv);
          }
        };

        // Register the load handler (and styledata as backup in case load already fired)
        map3d.current.on('load', onMapLoad);
        map3d.current.on('styledata', onMapLoad);

        // Catch Mapbox GL errors (style load failures, etc.)
        map3d.current.on('error', (e: any) => {
            console.error('[3D] Mapbox error:', e?.error?.message || e);
        });

        // Re-sync data once terrain + all tiles are fully rendered
        map3d.current.once('idle', () => {
            debugLog('[3D] map idle — re-syncing data');
            scheduleSync3DData();
        });
    };

    /**
     * Syncs current path/POI/AOI data to the 3D Mapbox GL map.
     * Sources + layers are pre-created in init3DMap; we only setData here.
     */
    const sync3DData = () => {
        const m = map3d.current;
        if (!m || !map3dLoaded.current) {
            debugLog('[3D] sync3DData: skipped', { hasMap: !!m, loaded: map3dLoaded.current });
            return;
        }

        // --- Paths ---
        const pathGeoJSON = buildPathsGeoJSON();
        debugLog('[3D] sync3DData: paths features:', pathGeoJSON.features.length);
        const pathSrc = m.getSource('paths-source') as mapboxgl.GeoJSONSource;
        if (pathSrc) pathSrc.setData(pathGeoJSON);

        // --- Path icon markers at midpoints ---
        const pathIconFeatures: any[] = [];
        pathGeoJSON.features.forEach((f: any) => {
            if (f.geometry?.type === 'LineString' && f.geometry.coordinates?.length > 1) {
                const coords = f.geometry.coordinates;
                const midIdx = Math.floor(coords.length / 2);
                const midCoord = coords[midIdx];

                // Use same icon as 2D map based on path category
                const pathObj = pathData.current?.[f.properties.id];
                const catId = pathObj?.pathCategoryId || PathCategoryId.Hiking;

                pathIconFeatures.push({
                    type: 'Feature',
                    properties: {
                        id: f.properties.id,
                        featureType: f.properties.featureType,
                        color: f.properties.color,
                        name: pathObj ? getTranslatedProperty(selectedLanguage.current, 'name', pathObj) : '',
                        iconImage: `path-cat-${catId}`,
                        iconOpacity: f.properties.iconOpacity,
                    },
                    geometry: { type: 'Point', coordinates: midCoord },
                });
            }
        });
        const pathIconSrc = m.getSource('path-icons-source') as mapboxgl.GeoJSONSource;
        if (pathIconSrc) pathIconSrc.setData({ type: 'FeatureCollection', features: pathIconFeatures });

        // --- Direction arrows for all visible paths (matches 2D behavior) ---
        const directionFeatures: any[] = [];
        if (pathDirection.current !== undefined) {
            pathGeoJSON.features.forEach((f: any) => {
                if (f.geometry?.type !== 'LineString' || f.geometry.coordinates?.length <= 1) {
                    return;
                }

                const coords = f.geometry.coordinates;
                const arrowCoords = pathDirection.current === PathDirection.Reverse
                    ? [...coords].reverse()
                    : coords;

                directionFeatures.push({
                    type: 'Feature',
                    properties: { id: f.properties.id },
                    geometry: { type: 'LineString', coordinates: arrowCoords },
                });
            });
        }
        const pathDirectionSrc = m.getSource('path-direction-source') as mapboxgl.GeoJSONSource;
        if (pathDirectionSrc) {
            pathDirectionSrc.setData({ type: 'FeatureCollection', features: directionFeatures });
        }

        // --- AOIs ---
        const aoiGeoJSON = buildAoisGeoJSON();
        debugLog('[3D] sync3DData: aoi features:', aoiGeoJSON.features.length);
        const aoiSrc = m.getSource('aois-source') as mapboxgl.GeoJSONSource;
        if (aoiSrc) aoiSrc.setData(aoiGeoJSON);

        // --- POIs ---
        const poiGeoJSON = buildPoisGeoJSON();
        debugLog('[3D] sync3DData: zoom:', m.getZoom(), 'poi features:', poiGeoJSON.features.length, ', noCluster:', poiGeoJSON.noClusterFeatures.length);
        // Debug: log what's in noClusterFeatures
        poiGeoJSON.noClusterFeatures.forEach((f: any, i: number) => {
            debugLog(`[3D] noCluster[${i}]:`, f.properties.id, 'icon:', f.properties.iconImage, 'badge:', !!f.properties.isEventBadge, 'rowIdx:', f.properties.eventRowIndex, 'coords:', f.geometry.coordinates);
        });
        const poiSrc = m.getSource('pois-source') as mapboxgl.GeoJSONSource;
        if (poiSrc) poiSrc.setData({ type: 'FeatureCollection', features: poiGeoJSON.features });
        const poiNoClusterSrc = m.getSource('pois-nocluster-source') as mapboxgl.GeoJSONSource;
        if (poiNoClusterSrc) poiNoClusterSrc.setData({ type: 'FeatureCollection', features: poiGeoJSON.noClusterFeatures });
        // Event badges go to their own source so they render on top of POIs
        const badgeFeatures = poiGeoJSON.noClusterFeatures.filter((f: any) => f.properties.isEventBadge);
        const badgeSrc = m.getSource('poi-event-badge-source') as mapboxgl.GeoJSONSource;
        if (badgeSrc) badgeSrc.setData({ type: 'FeatureCollection', features: badgeFeatures });

        // --- GPS position ---
        const gpsSrc = m.getSource('gps-source') as mapboxgl.GeoJSONSource;
        if (gpsSrc) {
            if (gpsPositionData.current) {
                const { longitude, latitude } = gpsPositionData.current;
                const headingDeg = headingData.current != null ? (headingData.current * 180 / Math.PI) : 0;
                gpsSrc.setData({
                    type: 'FeatureCollection',
                    features: [{
                        type: 'Feature',
                        properties: { heading: headingDeg },
                        geometry: { type: 'Point', coordinates: [longitude, latitude] }
                    }]
                });
            } else {
                gpsSrc.setData({ type: 'FeatureCollection', features: [] });
            }
        }
    };

    /**
     * Draws all the paths provided in pathData, filter with optional ID array
     */
    const drawPaths = (ids?: string[]) => {
        if (!pathData.current || !Object.keys(pathData.current).length) {
            return;
        }

        const crossCountrySkiingLineFeatures: Feature<Geometry>[] = [];
        const crossCountrySkiingIconFeatures: Feature<Geometry>[] = [];
        const crossCountrySkiingEndcapFeatures: Feature<Geometry>[] = [];
      
        const mountainBikingLineFeatures: Feature<Geometry>[] = [];
        const mountainBikingIconFeatures: Feature<Geometry>[] = [];
        const mountainBikingEndcapFeatures: Feature<Geometry>[] = [];
      
        const hikingLineFeatures: Feature<Geometry>[] = [];
        const hikingIconFeatures: Feature<Geometry>[] = [];
        const hikingEndcapFeatures: Feature<Geometry>[] = [];

        const runningLineFeatures: Feature<Geometry>[] = [];
        const runningIconFeatures: Feature<Geometry>[] = [];
        const runningEndcapFeatures: Feature<Geometry>[] = [];

        const ridingLineFeatures: Feature<Geometry>[] = [];
        const ridingIconFeatures: Feature<Geometry>[] = [];
        const ridingEndcapFeatures: Feature<Geometry>[] = [];

        const canoeLineFeatures: Feature<Geometry>[] = [];
        const canoeIconFeatures: Feature<Geometry>[] = [];
        const canoeEndcapFeatures: Feature<Geometry>[] = [];

        const chairLiftLineFeatures: Feature<Geometry>[] = [];
        const chairLiftIconFeatures: Feature<Geometry>[] = [];
        const chairLiftEndcapFeatures: Feature<Geometry>[] = [];

        const gondolaLineFeatures: Feature<Geometry>[] = [];
        const gondolaIconFeatures: Feature<Geometry>[] = [];
        const gondolaEndcapFeatures: Feature<Geometry>[] = [];

        const surfaceLiftLineFeatures: Feature<Geometry>[] = [];
        const surfaceLiftIconFeatures: Feature<Geometry>[] = [];
        const surfaceLiftEndcapFeatures: Feature<Geometry>[] = [];

        const beltLiftLineFeatures: Feature<Geometry>[] = [];
        const beltLiftIconFeatures: Feature<Geometry>[] = [];
        const beltLiftEndcapFeatures: Feature<Geometry>[] = [];

        const skiSlopeLineFeatures: Feature<Geometry>[] = [];
        const skiSlopeIconFeatures: Feature<Geometry>[] = [];
        const skiSlopeEndcapFeatures: Feature<Geometry>[] = [];

        // Sort paths so closed paths are rendered first (underneath open paths)
        const sortedPaths = getVisiblePaths(
            Object.values(pathData.current),
            pathGpsData.current,
            ids,
            highlightedType.current === FeatureType.Path ? highlightedId.current : undefined,
        ).sort((a, b) => {
            const aIsClosed = a.status === PathStatus.Closed ? 0 : 1;
            const bIsClosed = b.status === PathStatus.Closed ? 0 : 1;
            return aIsClosed - bIsClosed;
        });

        sortedPaths.forEach((path) => {
            if (!ids?.length || ids.includes(path.id)) {

                switch(path.pathCategoryId) {
                    case PathCategoryId.CrossCountrySkiing:
                        drawPath(
                            crossCountrySkiingLineFeatures,
                            crossCountrySkiingIconFeatures,
                            crossCountrySkiingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.MountainBiking:
                        drawPath(
                            mountainBikingLineFeatures,
                            mountainBikingIconFeatures,
                            mountainBikingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Hiking:
                        drawPath(
                            hikingLineFeatures,
                            hikingIconFeatures,
                            hikingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Running:
                        drawPath(
                            runningLineFeatures,
                            runningIconFeatures,
                            runningEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Riding:
                        drawPath(
                            ridingLineFeatures,
                            ridingIconFeatures,
                            ridingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Canoe:
                        drawPath(
                            canoeLineFeatures,
                            canoeIconFeatures,
                            canoeEndcapFeatures,
                            path,
                        );
                        break;
                    
                    case PathCategoryId.ChairLift:
                        drawPath(
                            chairLiftLineFeatures,
                            chairLiftIconFeatures,
                            chairLiftEndcapFeatures,
                            path,
                        );
                        break;  

                    case PathCategoryId.Gondola:
                        drawPath(
                            gondolaLineFeatures,
                            gondolaIconFeatures,
                            gondolaEndcapFeatures,
                            path,   
                        );
                        break;
                    
                    case PathCategoryId.SurfaceLift:
                        drawPath(
                            surfaceLiftLineFeatures,
                            surfaceLiftIconFeatures,
                            surfaceLiftEndcapFeatures,
                            path,
                        );
                        break;
                    
                    case PathCategoryId.BeltLift:
                        drawPath(
                            beltLiftLineFeatures,
                            beltLiftIconFeatures,
                            beltLiftEndcapFeatures,
                            path,
                        );
                        break;  

                    case PathCategoryId.SkiSlope:
                        drawPath(
                            skiSlopeLineFeatures,
                            skiSlopeIconFeatures,
                            skiSlopeEndcapFeatures,
                            path,
                        );
                        break;

                    default:
                        break;
                }
            }
        });

        // Add all the features for cross country skiing
        pathSource.current?.addFeatures([
          ...crossCountrySkiingLineFeatures,
          ...crossCountrySkiingEndcapFeatures,
        ]);
        crossCountrySkiingIconSource.current?.addFeatures([...crossCountrySkiingIconFeatures]);
        // Add all the features for mountain biking
        pathSource.current?.addFeatures([
          ...mountainBikingLineFeatures,
          ...mountainBikingEndcapFeatures,
        ]);
        mountainBikingIconSource.current?.addFeatures([...mountainBikingIconFeatures]);
        // Add all the features for hiking
        pathSource.current?.addFeatures([...hikingLineFeatures, ...hikingEndcapFeatures]);
        hikingIconSource.current?.addFeatures([...hikingIconFeatures]);
        // Add all the features for running
        pathSource.current?.addFeatures([...runningLineFeatures, ...runningEndcapFeatures]);
        runningIconSource.current?.addFeatures([...runningIconFeatures]);
        // Add all the features for riding
        pathSource.current?.addFeatures([...ridingLineFeatures, ...ridingEndcapFeatures]);
        ridingIconSource.current?.addFeatures([...ridingIconFeatures]);
        // Add all the features for canoe
        pathSource.current?.addFeatures([...canoeLineFeatures, ...canoeEndcapFeatures]);
        canoeIconSource.current?.addFeatures([...canoeIconFeatures]);
        // Add all the features for chair lift
        pathSource.current?.addFeatures([...chairLiftLineFeatures, ...chairLiftEndcapFeatures]);
        chairLiftIconSource.current?.addFeatures([...chairLiftIconFeatures]);
        // Add all the features for gondola
        pathSource.current?.addFeatures([...gondolaLineFeatures, ...gondolaEndcapFeatures]);
        gondolaIconSource.current?.addFeatures([...gondolaIconFeatures]);
        // Add all the features for surface lift
        pathSource.current?.addFeatures([...surfaceLiftLineFeatures, ...surfaceLiftEndcapFeatures]);
        surfaceLiftIconSource.current?.addFeatures([...surfaceLiftIconFeatures]);
        // Add all the features for belt lift
        pathSource.current?.addFeatures([...beltLiftLineFeatures, ...beltLiftEndcapFeatures]);
        beltLiftIconSource.current?.addFeatures([...beltLiftIconFeatures]);

        // Add all the features for ski slope
        pathSource.current?.addFeatures([...skiSlopeLineFeatures, ...skiSlopeEndcapFeatures]);
        skiSlopeIconSource.current?.addFeatures([...skiSlopeIconFeatures]);
    }

    /**
     * Draws the required features for a path to the provided feature lists
     */
    const drawPath = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        endcapFeatures: Feature<Geometry>[],
        path?: Path,
        isBold: boolean = false,
        direction?: PathDirection,
    ) => {
        if (!path) {
            return;
        }

        if (!pathCategoryProps.current) {
            return;
        }

        if (!pathGpsData.current || !pathGpsData.current[path.id]) {
            return;
        }
        const color = path.pathCategoryId === PathCategoryId.SkiSlope
            ? PROPS.PATHS.DIFFICULTY[path.difficulty]
            : pathCategoryProps.current[path.pathCategoryId].color;

        const {icon, closedIcon} = pathCategoryProps.current[path.pathCategoryId];
        const lineCoordinates = pathGpsData.current[path.id].map((coordinate: PathGpsCoordinate, index: number) =>
            fromLonLat([coordinate.longitude, coordinate.latitude, index]),
        );
        
        // Skip if no coordinates
        if (!lineCoordinates || lineCoordinates.length === 0) {
            return;
        }
        
        // Get status directly from path object (it's an enum, not a translated property)
        const pathStatus = path.status;
        const iconUrl = pathStatus !== PathStatus.Closed ? icon : closedIcon;
        
        // Add line features (path and arrows)
        lineFeatures.push(
            ...createCommonPathFeatures(
                lineCoordinates,
                path.id,
                COLORS.PATHS.LINE,
                pathStatus === PathStatus.Closed,
                isBold,
                direction,
                map.current?.getView()?.getZoom(),
            ),
        );
        
        // Add path type icons along the trail (always show, even for closed paths)
        const iconOpacity = pathStatus === PathStatus.Closed ? 0.5 : 1;
        iconFeatures.push(...createPathIconFeatures(lineCoordinates, iconUrl, color, 1000, iconOpacity));

        const endIndex = lineCoordinates.length - 1;
      
        const hasDarkBackground = mapType.current === MapType.Satelite;
        const startName = getTranslatedProperty(selectedLanguage?.current, 'start', path)
        const endName = getTranslatedProperty(selectedLanguage?.current, 'end', path)
        const endcapOpacity = pathStatus === PathStatus.Closed ? 0.3 : 1;
        endcapFeatures.push(createEndcapIconFeature(new Point(lineCoordinates[0]), startName, hasDarkBackground, color, endcapOpacity));
        endcapFeatures.push(createEndcapIconFeature(new Point(lineCoordinates[endIndex]), endName, hasDarkBackground, color, endcapOpacity));
    };

    /**
     * Draws all the paths provided in snowmobilePathData, filter with optional ID array
     */
    const drawSnowmobilePaths = (ids?: string[]) => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        if (ids?.length) {
            ids.forEach((id) => {
                const path = snowmobilePathData.current[id];
                if (!path) {
                    return;
                }
        
                path.subPaths.forEach((subPath) => {
                    if (typeof subPath === 'string') {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, snowmobileSubPathData.current[subPath]);
                    }
                    else {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                    }
                });
            });
        } else {
            Object.values(snowmobileSubPathData.current).forEach((subPath) => {
                drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
            });
        }
      
        snowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
        snowmobileIconSource.current?.addFeatures([]); // NOTE: Empty at the moment as many paths icons for crossings overlap, resulting in always being clustered
    };

    /**
     * Draws all the subpaths provided in snowmobileSubPathData, filter with optional ID array
     */
    const drawSnowmobileSubPaths = (ids?: string[]) => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        if (ids?.length) {
            Object.values(snowmobileSubPathData.current).forEach((subPath) => {
                if (ids.includes(subPath.id)) {
                    drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                }
            });
        }
      
        snowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
        snowmobileIconSource.current?.addFeatures([]); // NOTE: Empty at the moment as many paths icons for crossings overlap, resulting in always being clustered
    };

    /**
     * Draws the required features for a snowmobile subpath to the provided feature lists
     */
    const drawSnowmobileSubPath = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        subPath?: SnowmobileSubPath,
        isBold: boolean = false,
    ) => {
        if (!subPath) {
            return;
        }

        const lineCoordinates = subPath.subPathGPSCoordinates.map((coordinate) =>
            fromLonLat([coordinate.longitude, coordinate.latitude]),
        );
        
        // Skip if no coordinates
        if (!lineCoordinates || lineCoordinates.length === 0) {
            return;
        }
      
        lineFeatures.push(
            ...createSnowmobilePathFeatures(
                lineCoordinates,
                subPath.id,
                subPath.status === PathStatus.Closed,
                isBold,
            ),
        );
      
        if (subPath.start) {
            iconFeatures.push(
                createSnowmobilingIconFeature(new Point(lineCoordinates[0]), subPath.start, isBold),
            );
        }
      
        if (subPath.end) {
            const endIndex = lineCoordinates.length - 1;
            iconFeatures.push(
                createSnowmobilingIconFeature(new Point(lineCoordinates[endIndex]), subPath.end, isBold),
            );
        }
    };

    /**
     * Draws all the POIs provided in poiData, filter with optional ID array
     */
    const drawPois = (ids?: string[]) => {
        const connectorFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];
        const eventGroupFeatures: Feature<Geometry>[] = [];

        const visiblePois = Object.values(poiData.current).filter((poi) => !ids?.length || ids.includes(poi.id));
        const { groups, groupedEventIds } = groupLinkedEvents(visiblePois);
        poiEventRowLayout.current = {};

        visiblePois.forEach((poi) => {
            if (!groupedEventIds.has(`${poi.id}`)) {
                drawPoi(iconFeatures, poi, false, !!groups[`${poi.id}`]?.length);
            }
        });

        Object.entries(groups).forEach(([poiId, events]) => {
            const poi = poiData.current[poiId];
            const coordinates = fromLonLat([Number(poi.longitude), Number(poi.latitude)]);
            const color = poiCategoryProps.current?.[poi.poiCategoryId]?.color || COLORS.POIS.DEFAULT || '#19456B';

            connectorFeatures.push(createPoiEventConnectorFeature(new Point(coordinates), events.length, color));
            events.forEach((event, index) => {
                const feature = createPoiEventRowFeature(event, coordinates, index);
                if (feature) {
                    poiEventRowLayout.current[`${event.id}`] = { poiId, index };
                    eventGroupFeatures.push(feature);
                }
            });
            eventGroupFeatures.push(createPoiEventCountBadgeFeature(new Point(coordinates), poi.id, events.length, color));
        });

        // Connectors first so they are drawn beneath the POI and event icons
        poiSource.current?.addFeatures([...connectorFeatures, ...iconFeatures, ...eventGroupFeatures]);
    };

    /**
     * Groups events that borrow their linked POI's position under that POI. Only the next
     * upcoming or ongoing events are kept; the rest of the linked events are not drawn.
     */
    const groupLinkedEvents = (visiblePois: Poi[]) => {
        const visibleIds = new Set(visiblePois.map((poi) => `${poi.id}`));
        const groups: Record<string, Poi[]> = {};
        const groupedEventIds = new Set<string>();
        const now = Date.now();

        visiblePois.forEach((item) => {
            const event = item as any;
            // After toPois() conversion, the linked POI id is in linkedPoiId (string)
            const linkedPoiId = event.linkedPoiId ?? event.pointOfInterestId;
            if (event.sourceType !== 'event' || linkedPoiId == null) {
                return;
            }
            const poiId = `${linkedPoiId}`;
            const poi = poiData.current[poiId];
            if (!visibleIds.has(poiId) || !poi || !isValidLonLat(poi.longitude, poi.latitude)) {
                return;
            }
            // Events saved with (almost) the same position as their POI would be drawn on top of it
            const sharesPoiPosition = event.positionFromPoi || (
                isValidLonLat(event.longitude, event.latitude) &&
                getDistance(
                    [Number(event.longitude), Number(event.latitude)],
                    [Number(poi.longitude), Number(poi.latitude)],
                ) <= POI_EVENT_SAME_POSITION_METERS
            );
            if (!sharesPoiPosition) {
                return;
            }
            groupedEventIds.add(`${item.id}`);
            if (isUpcomingEvent(event, now)) {
                (groups[poiId] ??= []).push(item);
            }
        });

        Object.keys(groups).forEach((poiId) => {
            groups[poiId] = groups[poiId]
                .sort((a, b) => getEventStartTime(a) - getEventStartTime(b))
                .slice(0, MAX_POI_EVENTS);
        });

        return { groups, groupedEventIds };
    };

    /**
     * Creates the feature for an event placed in the row below its POI
     */
    const createPoiEventRowFeature = (
        event: Poi,
        poiCoordinates: number[],
        index: number,
        isBold: boolean = false,
        withGroupRole: boolean = true,
    ): Feature<Geometry> | undefined => {
        const categoryProps = poiCategoryProps.current?.[event.poiCategoryId];
        if (!categoryProps) {
            return undefined;
        }
        const name = truncateLabel(getTranslatedProperty(selectedLanguage?.current, 'name', event) ?? '');
        const date = formatEventDate((event as any).startAt);
        const label = [name, date].filter(Boolean).join('\n');
        return createPoiEventRowIconFeature(
            new Point(poiCoordinates),
            index,
            event.id,
            label,
            categoryProps.color,
            categoryProps.icon,
            mapType.current === MapType.Satelite,
            isBold,
            withGroupRole,
        );
    };

    /**
     * Draws the required features for a POI to the provideed feature lists
     */
    const drawPoi = (
        iconFeatures: Feature<Geometry>[],
        poi?: Poi,
        isBold: boolean = false,
        hasLinkedEvents: boolean = false,
    ) => {
        if (!poi) {
            return;
        }

        if (!poiCategoryProps.current) {
            return;
        }
        
        // Skip unknown POI categories gracefully
        const categoryProps = poiCategoryProps.current[poi.poiCategoryId];
        if (!categoryProps) {
            console.warn(`Skipping POI ${poi.id}: unknown category ${poi.poiCategoryId}`);
            return;
        }
        
        // Items without a location (e.g. events with null coordinates) would otherwise be
        // projected to 0,0 and pull the fit-all extent towards the Gulf of Guinea.
        if (!isValidLonLat(poi.longitude, poi.latitude)) {
            console.warn(`Skipping POI ${poi.id}: missing or invalid coordinates`);
            return;
        }

        const {color, icon} = categoryProps;
        const coordinates = fromLonLat([Number(poi.longitude), Number(poi.latitude)]);
      
        const hasDarkBackground = mapType.current === MapType.Satelite;
        
        // Check if this is a temperature POI (POI_CATEGORY_19)
        // poiCategoryId is a string after conversion from API response
        if (poi.poiCategoryId === '19') {
            // Extract temperature from POI description
            // Description might be a string (JSON) or an object
            let temperature = 0;
            
            if (typeof poi.description === 'string') {
                try {
                    const parsed = JSON.parse(poi.description);
                    temperature = parsed?.temperature ?? 0;
                } catch (e) {
                    temperature = 0;
                }
            } else if (typeof poi.description === 'object' && poi.description !== null) {
                temperature = (poi.description as any)?.temperature ?? 0;
            }
            
            const tempFeature = createTemperaturePoiIconFeature(new Point(coordinates), poi.id, temperature, color, icon, hasDarkBackground, isBold);
            tempFeature.set('__isTemperature', true); // Mark as temperature POI for zoom handling
            iconFeatures.push(tempFeature);
        } else {
            const name = getTranslatedProperty(selectedLanguage?.current, 'name', poi)
            iconFeatures.push(
                createCommonPoiIconFeature(
                    new Point(coordinates),
                    poi.id,
                    name,
                    color,
                    icon,
                    hasDarkBackground,
                    isBold,
                    hasLinkedEvents ? 60 : 42,
                ),
            );
        }
    };

    /**
     * Draws all the AOIs provided in aoiData, filter with optional ID array
     */
    const drawAois = (ids?: string[]) => {
        const iconFeatures: Feature<Geometry>[] = [];
        const lineFeatures: Feature<Geometry>[] = [];    

        Object.values(aoiData.current).forEach((aoi) => {
            if (!ids?.length || ids.includes(aoi.id)) {
                drawAoi(lineFeatures, iconFeatures, aoi);
            }
        });
        
        aoiSource.current?.addFeatures([...iconFeatures]);
    };

    /**
     * Draws the required features for a aoi to the provided feature lists
     */
    const drawAoi = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        aoi?: Aoi,
        isBold: boolean = false,
    ) => {
        if (!aoi) {
            return;
        }

        if (!aoiCategoryProps.current) {
            return;
        }

        if (!aoiGpsData.current || !aoiGpsData.current[aoi.id]) {
            return;
        }
        
        // Skip unknown AOI categories gracefully
        const categoryProps = aoiCategoryProps.current[aoi.aoiCategoryId];
        if (!categoryProps) {
            console.warn(`Skipping AOI ${aoi.id}: unknown category ${aoi.aoiCategoryId}`);
            return;
        }

        const {color, icon} = categoryProps;
        const lineCoordinates = aoiGpsData.current[aoi.id]
            .filter((coordinate: AoiGpsCoordinate) => isValidLonLat(coordinate.longitude, coordinate.latitude))
            .map((coordinate: AoiGpsCoordinate, index: number) =>
                fromLonLat([Number(coordinate.longitude), Number(coordinate.latitude), index]),
            );
        if (!lineCoordinates.length) {
            return;
        }
     
        lineFeatures.push(
            ...createCommonAoiFeatures(
                lineCoordinates,
                aoi.id,
                color,
            ),
        );

        const xCoordinates = lineCoordinates.map((coordinate) => coordinate[0]);
        xCoordinates.sort((a, b) => a - b);
        const yCoordinates = lineCoordinates.map((coordinate) => coordinate[1]);
        yCoordinates.sort((a, b) => a - b);
        const center = [
          (xCoordinates[0] + xCoordinates[xCoordinates.length - 1]) / 2,
          (yCoordinates[yCoordinates.length - 1] + yCoordinates[0]) / 2,
        ];

        const hasDarkBackground = mapType.current === MapType.Satelite;
        const name = getTranslatedProperty(selectedLanguage?.current, 'name', aoi);
        iconFeatures.push(
            createCommonAoiIconFeature(new Point(center), aoi.id, name, color, icon, hasDarkBackground, isBold),
        );
    };

    /**
     * Draws the required features for the current selection to the highlight source
     */
    const redrawHighlight = () => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        highlightedPathSource.current?.clear();
        highlightedPathIconSource.current?.clear();
        highlightedSnowmobilePathSource.current?.clear();
        highlightedPoiSource.current?.clear();
        highlightedAoiSource.current?.clear();

        highlightedPathLayer.current?.setVisible(false);
        highlightedPathIconLayer.current?.setVisible(false);
        highlightedSnowmobilePathLayer.current?.setVisible(false);
        highlightedPoiLayer.current?.setVisible(false);
        highlightedAoiLayer.current?.setVisible(false);
        
        if (!highlightedId.current) {
            return;
        }
      
        switch (highlightedType.current) {
            case FeatureType.Path:
                const path = pathData.current[`${highlightedId.current}`];
                if (!path) {
                    break;
                }
                const endcapFeatures: Feature<Geometry>[] = [];
                const childPaths = (path.compositeChildPathIds ?? [])
                    .filter(id => String(id) !== String(path.id))
                    .map(id => pathData.current[String(id)])
                    .filter(child => child && pathGpsData.current[child.id]?.length > 0);
                const highlightedPaths = childPaths.length ? childPaths : [path];
                highlightedPaths.forEach(highlightedPath => {
                    drawPath(lineFeatures, iconFeatures, endcapFeatures, highlightedPath, true, pathDirection.current);
                });
                highlightedPathSource.current?.addFeatures([...lineFeatures, ...endcapFeatures]);
                highlightedPathIconSource.current?.addFeatures(iconFeatures);
                highlightedPathLayer.current?.setVisible(true);
                highlightedPathIconLayer.current?.setVisible(true);
                break;
        
            case FeatureType.SnowmobilePath:
                const snowmobilePath = snowmobilePathData.current[`${highlightedId.current}`];
                if (!snowmobilePath) {
                    break;
                }
                snowmobilePath.subPaths.forEach((subPath) => {
                    if (typeof subPath === 'string') {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, snowmobileSubPathData.current[subPath], true);
                    }
                    else {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                    }
                });
                highlightedSnowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedSnowmobilePathLayer.current?.setVisible(true);
                break;
        
            case FeatureType.SnowmobileSubPath:
                const snowmobileSubPath = snowmobileSubPathData.current[highlightedId.current];
                if (!snowmobileSubPath) {
                    break;
                }
                drawSnowmobileSubPath(
                    lineFeatures,
                    iconFeatures,
                    snowmobileSubPath,
                    true,
                );
                highlightedSnowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedSnowmobilePathLayer.current?.setVisible(true);
                break;
        
            case FeatureType.Poi:
                const poi = poiData.current[`${highlightedId.current}`];
                if (!poi) {
                    break;
                }
                const eventRowLayout = poiEventRowLayout.current[`${poi.id}`];
                const parentPoi = eventRowLayout ? poiData.current[eventRowLayout.poiId] : undefined;
                const eventRowFeature = parentPoi && createPoiEventRowFeature(
                    poi,
                    fromLonLat([Number(parentPoi.longitude), Number(parentPoi.latitude)]),
                    eventRowLayout.index,
                    true,
                    false,
                );
                if (eventRowFeature) {
                    iconFeatures.push(eventRowFeature);
                } else {
                    drawPoi(
                        iconFeatures,
                        poi,
                        true,
                        Object.values(poiEventRowLayout.current).some((row) => row.poiId === `${poi.id}`),
                    );
                }
                highlightedPoiSource.current?.addFeatures(iconFeatures);
                highlightedPoiLayer.current?.setVisible(true);
                break;

            case FeatureType.Aoi:
                const aoi = aoiData.current[`${highlightedId.current}`];
                if (!aoi) {
                    break;
                }
                drawAoi(lineFeatures, iconFeatures, aoi, true);
                highlightedAoiSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedAoiLayer.current?.setVisible(true);
                break;
        
            default:
                break;
        }

        if (isThreeDMapType(mapType.current)) {
            scheduleSync3DData();
        }
    };

    /**
     * Recenters the selected feature, recenters all content if no selection is provided
     */
    const refocus = () => {
        debugLog('[MAP DEBUG] refocus() called', {
            selectedType: selectedType.current,
            selectedId: selectedId.current,
            focusData: focusData.current,
            filterData: filterData.current,
        });
        const sync3DCameraTo2DFocus = (extentToFit?: number[]) => {
            if (!isThreeDMapType(mapType.current) || !map3d.current) {
                return;
            }

            if (extentToFit && extentToFit.length === 4 && !isEmpty(extentToFit as any)) {
                const southWest = toLonLat([extentToFit[0], extentToFit[1]]);
                const northEast = toLonLat([extentToFit[2], extentToFit[3]]);

                map3d.current.fitBounds([
                    [southWest[0], southWest[1]],
                    [northEast[0], northEast[1]],
                ], {
                    padding: 100,
                    maxZoom: selectedType.current === FeatureType.Poi ? 16 : 16,
                    duration: 500,
                });
                return;
            }

            const view = map.current?.getView();
            const center = view?.getCenter();
            const zoom = view?.getZoom();

            if (!center || zoom === undefined) {
                return;
            }

            const [longitude, latitude] = toLonLat(center);
            map3d.current.easeTo({
                center: [longitude, latitude],
                zoom,
                duration: 500,
            });
        };

        const source = getSourceForType(selectedType.current);
        const isPathSelection = selectedType.current === FeatureType.Path && selectedId.current !== undefined;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

        if (isPathSelection) {
            map.current?.getView().cancelAnimations();
        }
        
        const options: FitOptions = {
            padding: [100, 100, 100, 100],
            maxZoom: selectedType.current === FeatureType.Poi ? 16 : 16,
            duration: isPathSelection && !reduceMotion ? 500 : undefined,
        };

        if (source?.getFeatures().length) {
            if (selectedId.current !== undefined) {
                let extent = createEmpty();
                let hasExtent = false;
                let didFitSelection = false;

                if (selectedType.current === FeatureType.Path) {
                    const selectedPath = pathData.current[`${selectedId.current}`] as any;
                    const selectedPathIds = new Set<string>([`${selectedId.current}`]);

                    (selectedPath?.compositeChildPathIds ?? []).forEach((childId: number | string) => {
                        selectedPathIds.add(`${childId}`);
                    });

                    // Fallback for payloads that include compositions but not flattened child ids.
                    (selectedPath?.pathCompositions ?? []).forEach((composition: any) => {
                        if (composition?.childPathId !== undefined && composition?.childPathId !== null) {
                            selectedPathIds.add(`${composition.childPathId}`);
                        }
                    });

                    selectedPathIds.forEach((pathId) => {
                        const coordinates = pathGpsData.current[pathId] ?? [];
                        coordinates.forEach((coordinate: PathGpsCoordinate) => {
                            if (Number.isFinite(coordinate.longitude) && Number.isFinite(coordinate.latitude)) {
                                const projected = fromLonLat([coordinate.longitude, coordinate.latitude]);
                                extend(extent, [projected[0], projected[1], projected[0], projected[1]]);
                                hasExtent = true;
                            }
                        });
                    });

                    if (hasExtent && !isEmpty(extent)) {
                        map.current?.getView().fit(extent, options);
                        sync3DCameraTo2DFocus(extent as any);
                        didFitSelection = true;
                    }
                }

                if (didFitSelection) {
                    return;
                }
        
                if (selectedType.current === FeatureType.SnowmobilePath) {
                    const subPathIds = snowmobilePathData.current[selectedId.current]?.subPaths;
                    const selectedSubPathIds = new Set((subPathIds ?? []).map((subPathId) => `${subPathId}`));
            
                    if (subPathIds?.length) {
                        source.getFeatures().forEach((feature) => {
                            const type = feature.get(OBJECT_TYPE);
                            const id = feature.get(OBJECT_ID);

                            if (type === FeatureType.SnowmobileSubPath && selectedSubPathIds.has(`${id}`)) {
                                extend(extent, feature!.getGeometry()!.getExtent());
                                hasExtent = true;
                            }
                        });
                    }
                } else {
                    const selectedIds = new Set<string>([`${selectedId.current}`]);

                    if (selectedType.current === FeatureType.Path) {
                        const selectedPath = pathData.current[`${selectedId.current}`] as any;
                        (selectedPath?.compositeChildPathIds ?? []).forEach((childId: number | string) => {
                            selectedIds.add(`${childId}`);
                        });
                    }

                    source.getFeatures().forEach((feature) => {
                        const type = feature.get(OBJECT_TYPE);
                        const id = feature.get(OBJECT_ID);
                    
                        if (type === selectedType.current && selectedIds.has(`${id}`)) {
                            extend(extent, feature!.getGeometry()!.getExtent());
                            hasExtent = true;
                        }
                    });
                }

                if (hasExtent && !isEmpty(extent)) {
                    map.current?.getView().fit(extent, options);
                    sync3DCameraTo2DFocus(extent as any);
                }
            } else {
                const extent = source.getExtent();
                map.current?.getView().fit(extent, options);
                sync3DCameraTo2DFocus(extent as any);
            }
        } else {
            const extent = createEmpty();
            if (pathSource.current && snowmobilePathSource.current && poiSource.current && aoiSource.current) {
                // Use focusData if available, otherwise use filterData
                const dataToUse = focusData.current?.length ? focusData.current : filterData.current;
                
                if (dataToUse?.length) {
                    dataToUse.forEach((group) => {
                        const {type: groupType, ids} = group;
                        const groupSource = getSourceForType(groupType);

                        groupSource?.getFeatures().forEach((feature) => {
                            const type = feature.get(OBJECT_TYPE);
                            const id = feature.get(OBJECT_ID);
                        
                            if (type === groupType && ids.includes(id)) {
                                extend(extent, feature!.getGeometry()!.getExtent());
                            }
                        });
                    });
                }
                else {
                    extend(extent, pathSource.current.getExtent());
                    extend(extent, snowmobilePathSource.current.getExtent());
                    extend(extent, poiSource.current.getExtent());
                    extend(extent, aoiSource.current.getExtent());
                }
            }
            if (!isEmpty(extent)) {
                map.current?.getView().fit(extent, options);
                sync3DCameraTo2DFocus(extent as any);
            }
        }
        
    };

    /**
     * Gets the source for a given feature type
     */
    const getSourceForType = (type?: string): VectorSource<Feature<Geometry>> | undefined => {
        switch (type) {
            case FeatureType.Path:
                return pathSource.current;
        
            case FeatureType.SnowmobilePath:
            case FeatureType.SnowmobileSubPath:
                return snowmobilePathSource.current;
        
            case FeatureType.Poi:
                return poiSource.current;

            case FeatureType.Aoi:
                return aoiSource.current;
        
            default:
                return undefined;
        }
    };

    /**
     * Gets the distance between two coordinates
     */
    const getCoordsDistance = (coordinate1: number[], coordinate2: number[], projection?: string): number => {
        projection = projection || 'EPSG:4326';
    
        const sourceProj = map.current?.getView().getProjection();
        const c1 = transform(coordinate1, sourceProj, projection);
        const c2 = transform(coordinate2, sourceProj, projection);
    
        return getDistance(c1, c2);
    };

    /**
     * Posts a message about the feature nearest to a GPS coordinate
     */
    const reportNearestFeature = (coordinate: number[]) => {
        const allPathFeatures = pathSource.current?.getFeatures() ?? [];

        // If a path is currently selected, calculate nearest point only on that path.
        // This avoids selecting overlapping composite/child siblings and losing the graph marker.
        let closestFeature: Feature<Geometry> | undefined;
        if (selectedType.current === FeatureType.Path && selectedId.current) {
            let bestDistance = Number.POSITIVE_INFINITY;

            allPathFeatures.forEach((feature) => {
                if (feature.get(OBJECT_TYPE) !== FeatureType.Path) {
                    return;
                }
                if (`${feature.get(OBJECT_ID)}` !== `${selectedId.current}`) {
                    return;
                }

                const geometry = feature.getGeometry();
                if (!geometry) {
                    return;
                }

                const candidatePoint = geometry.getClosestPoint(coordinate);
                const candidateDistance = getCoordsDistance(candidatePoint, coordinate);

                if (candidateDistance < bestDistance) {
                    bestDistance = candidateDistance;
                    closestFeature = feature;
                }
            });
        }

        // Fallback to previous behavior if no selected feature could be matched.
        const fallbackFeature = pathSource.current?.getClosestFeatureToCoordinate(coordinate) ?? undefined;
        closestFeature = closestFeature ?? fallbackFeature;

        if (closestFeature) {
            const closestPoint = closestFeature!
                .getGeometry()!
                .getClosestPoint(coordinate);

            if (getCoordsDistance(closestPoint, coordinate) > 70) {
                return;
            }

            const objectId = closestFeature.get(OBJECT_ID);
            if (!objectId) {
                return;
            }

            const latLongCoords = toLonLat(closestPoint);
            const msg = JSON.stringify({
                pathId: parseInt(objectId, 10),
                pointCoordinate: {
                    x: latLongCoords[0],
                    y: latLongCoords[1],
                    ...(Number.isFinite(closestPoint[2]) && {index: closestPoint[2]}),
                },
            });
            postMessage(msg);
            onMessage?.(msg);
        } else {
            const msg = JSON.stringify({
                pathId: 0,
                pointCoordinate: {
                    x: 0,
                    y: 0,
                },
            });
            postMessage(msg);
            onMessage?.(msg);
        }
    };

    return (
        <>
            <div ref={mapRef} className="map"></div>
            <div ref={map3dRef} className="map map-3d" style={{display: 'none'}}></div>
        </>
    )
};

export default forwardRef(MapComponent);